import { createSelector } from 'reselect';
import _isEmpty from 'lodash/isEmpty';
import { getCurrentStore, getSisFlag } from './metaData';
import { getValue } from '../utils/objectUtils';
import defaultFlags, { flags } from '../../initFlags/featureFlags';
import { isMobile } from '../utils/deviceTypeUtils';
import { getFeatureVariant } from '../organism/ABFeature/Feature';
import { FLEEK_STORE, LUXE_STORE } from '../../constants/constant';
import { getUserCohortIdsForClient } from '../../utils/products';
import { getAmplitudeVariantPayload } from '../../utils/amplitudeCommon';

const getFeatureFlags = (
  state: import('../types/rootState').RootState
): import('../types/firebaseFlags').Flags => state.flags;

const getNewSizeRevampFlag = createSelector(
  getFeatureFlags,
  (flags = {}) => (flags.newSizeRevampFlag || 'OFF') === 'ON'
);

export const getReferralFeatureEnableFlag = createSelector(
  getFeatureFlags,
  flags => (flags.referralFeatureEnable || 'OFF') === 'ON'
);

export const getReferrerEnableFlag = createSelector(
  getReferralFeatureEnableFlag,
  getFeatureFlags,
  (featureFlag, flags) => featureFlag && (flags.referrerEnable || 'OFF') === 'ON'
);

export const getRefereeEnableFlag = createSelector(
  getReferralFeatureEnableFlag,
  getFeatureFlags,
  (featureFlag, flags) => featureFlag && (flags.refereeEnable || 'OFF') === 'ON'
);

export const getDisplayReferralNewTagFlag = createSelector(
  getReferralFeatureEnableFlag,
  getFeatureFlags,
  (featureFlag, flags) => featureFlag && (flags.displayReferralNewTag || 'OFF') === 'ON'
);

export const getDisplayAjioWalletNewTagFlag = createSelector(
  getFeatureFlags,
  flags => (flags.displayAjioWalletNewTag || 'OFF') === 'ON'
);

export const getDisplayMyRewardsNewTagFlag = createSelector(
  getFeatureFlags,
  flags => (flags.displayMyRewardsNewTag || 'OFF') === 'ON'
);

export const getDisplayJioPrimePointsNewTagFlag = createSelector(
  getFeatureFlags,
  flags => (flags.displayJioPrimePointsNewTag || 'OFF') === 'ON'
);

export const getDisplayAjioWalletTotalAmountFlag = createSelector(
  getReferralFeatureEnableFlag,
  getFeatureFlags,
  (featureFlag, flags) => featureFlag && (flags.displayAjioWalletTotalAmount || 'OFF') === 'ON'
);

export const getUtmParamsFromConfig = createSelector(getFeatureFlags, (flags = {}) => {
  const { referralShareUtmParams } = flags;
  return referralShareUtmParams;
});

export const getReferralShareMessage = createSelector(getFeatureFlags, (flags = {}) => {
  const { referralShareMessage = '' } = flags;
  return referralShareMessage;
});

const getDisableGSTContentFlag = createSelector(getFeatureFlags, (flags = {}) => {
  const { disableGSTContent } = flags;
  return disableGSTContent;
});

const getDefaultGSTMessage = createSelector(getFeatureFlags, (flags = {}) => {
  const { defaultGSTMessage } = flags;
  return defaultGSTMessage;
});

export const getPDPReferrerWidgetEnableFlag = createSelector(
  getReferralFeatureEnableFlag,
  getReferrerEnableFlag,
  getFeatureFlags,
  (featureFlag, referrerEnabled, flags) =>
    featureFlag && referrerEnabled && (flags.referralWidgetPDPEnable || 'OFF') === 'ON'
);

export const getThankYouReferrerWidgetEnableFlag = createSelector(
  getReferralFeatureEnableFlag,
  getReferrerEnableFlag,
  getFeatureFlags,
  (featureFlag, referrerEnabled, flags) =>
    featureFlag && referrerEnabled && (flags.referralWidgetThankYouPageEnable || 'OFF') === 'ON'
);

export const getUseRevampedGoogleAuthFlag = createSelector(
  getFeatureFlags,
  flags => (flags.revampedGoogleAuth || 'OFF') === 'ON'
);

export const getOrderFilterFlag = createSelector(
  getFeatureFlags,
  flags => (flags.orderFilterFlag || 'OFF') === 'ON'
);

export const getShowCarrierInfo = createSelector(
  getFeatureFlags,
  flags => (flags.showCarrierInfo || 'OFF') === 'ON'
);

export const getCarrierInfoTimestamp = createSelector(getFeatureFlags, (flags = {}) => {
  const { carrierInfoTimestamp = 15 } = flags;
  return carrierInfoTimestamp;
});

const getMarkAsDeliveredFlag = createSelector(
  getFeatureFlags,
  ({ markAsDeliveredFlag }) => markAsDeliveredFlag === 'ON'
);

export const getSiteNameToFindIfscCode = createSelector(
  getFeatureFlags,
  ({ siteNameToFindIfscCode }) => siteNameToFindIfscCode
);
export const getSiteUrlToFindIfscCode = createSelector(
  getFeatureFlags,
  ({ siteUrlToFindIfscCode }) => siteUrlToFindIfscCode
);
export const getBlockNotFoundIfscCodeSubmitFlag = createSelector(
  getFeatureFlags,
  ({ blockNotFoundIfscCodeSubmit }) => blockNotFoundIfscCodeSubmit === 'ON'
);
export const getShowBrandSizeFlag = createSelector(
  getFeatureFlags,
  flags => (flags.showBrandSizeFlag || 'OFF') === 'ON'
);

const getRuntimeBannersMsiteFlag = createSelector(
  getFeatureFlags,
  flags => (flags.runtimeBannersMsite || 'OFF') === 'ON'
);

const getRuntimeBannersMsiteSisFlag = createSelector(
  getFeatureFlags,
  flags => (flags.runtimeBannersMsiteSis || 'OFF') === 'ON'
);

const getRuntimeBannersMsiteWithSisFlag = createSelector(
  getFeatureFlags,
  getSisFlag,
  (flags, isSis) => !isSis && (flags.runtimeBannersMsite || 'OFF') === 'ON'
);
const getjewelReturnMessageFlag = createSelector(
  getFeatureFlags,
  flags => (flags.jewelReturnMessageFlag || 'OFF') === 'ON'
);

const getjewelReturnModalMessage = createSelector(
  getFeatureFlags,
  ({ jewelReturnModalMessage }) => jewelReturnModalMessage
);

const getReturnCapture = createSelector(getFeatureFlags, flags => flags.returnCapture || {});
const getLuxeFlags = createSelector(
  getFeatureFlags,
  flags =>
    (flags && flags.luxeFlags) || {
      isThemeEnable: 'ON',
      isFooterEnabled: 'ON',
      footerData: [
        {
          isEnable: true,
          title: 'Customer Service',
          link: '/selfcare'
        },
        {
          isEnable: true,
          title: 'FAQs',
          link: '/selfcare'
        },
        {
          isEnable: false,
          title: 'Terms & Conditions',
          link: '/help/termsAndCondition'
        },
        {
          isEnable: false,
          title: 'Returns & Refunds policy',
          link: '/return-refund-policy'
        }
      ]
    }
);

export const getReferralWidgetHeading = createSelector(
  getFeatureFlags,
  flags => flags.referralWidgetHeading || ''
);
export const getReferralWidgetSubHeading = createSelector(
  getFeatureFlags,
  flags => flags.referralWidgetSubHeading || ''
);
export const getReferralMyAccountLinkTitle = createSelector(
  getFeatureFlags,
  flags => flags.referralMyAccountLinkTitle || ''
);
export const getReferralMyAccountLinkSubTitle = createSelector(
  getFeatureFlags,
  flags => flags.referralMyAccountLinkSubTitle || ''
);
export const getReferralLandingPageHeading = createSelector(
  getFeatureFlags,
  flags => flags.referralLandingPageHeading || ''
);
export const getReferralCongratulationHeading = createSelector(
  getFeatureFlags,
  flags => flags.referralCongratulationHeading || ''
);
export const getReferralCongratulationSubHeading = createSelector(
  getFeatureFlags,
  flags => flags.referralCongratulationSubHeading || ''
);
export const getReferralAuthFeedbackTitle = createSelector(
  getFeatureFlags,
  flags => flags.referralAuthFeedbackTitle || ''
);
export const getReferralAuthFeedbackSubTitle = createSelector(
  getFeatureFlags,
  flags => flags.referralAuthFeedbackSubTitle || ''
);

export const getHurryMsgInvCap = createSelector(
  getFeatureFlags,
  flags => flags.hurryMsgInvCap || 10
);

export const getTopShopperGrandPriceBannerImagePath = createSelector(
  getFeatureFlags,
  flags => flags.topShopperGrandPriceBannerImagePath || ''
);

export const getTopShopperGrandPriceBannerHyperlink = createSelector(
  getFeatureFlags,
  flags => flags.topShopperGrandPriceBannerHyperlink || ''
);

export const getTopShopperTermsHyperlink = createSelector(
  getFeatureFlags,
  flags => flags.topShopperTermsHyperlink || ''
);

export const getTopShopperShopMoreHyperlink = createSelector(
  getFeatureFlags,
  flags => flags.topShopperShopMoreHyperlink || ''
);

export const getPriceRevealMobileFeatureFlag = createSelector(
  getFeatureFlags,
  flags => (flags.priceRevealMobileFeatureFlag || 'OFF') === 'ON'
);

const getPriceRevealV2MobileFeatureFlag = createSelector(
  getFeatureFlags,
  flags => (flags?.priceRevealV2MobileFeatureFlag || 'OFF') === 'ON'
);

const getShoppingAssistantFlag = createSelector(
  getFeatureFlags,
  flags => (flags?.shoppingAssistantFlag || 'OFF') === 'ON'
);
const defaultShoppingAssistantData = () => getValue(defaultFlags, 'flags.shoppingAssistantData');

const getShoppingAssistantData = createSelector(
  getFeatureFlags,
  defaultShoppingAssistantData,
  (flags, defaultValue) => flags.shoppingAssistantData || defaultValue || {}
);

const defaultPriceRevealMobileData = () => getValue(defaultFlags, 'flags.priceRevealMobileData');

const getPriceRevealMobileData = createSelector(
  getFeatureFlags,
  defaultPriceRevealMobileData,
  (flags, defaultValue) => flags.priceRevealMobileData || defaultValue || {}
);

const getPriceRevealLUXEFlag = createSelector(
  getFeatureFlags,
  flags => (flags.priceRevealLUXEFlag || 'OFF') === 'ON'
);

const getEGiftCard = createSelector(getFeatureFlags, flags => (flags.eGiftCard || 'OFF') === 'ON');

const getEnablePwaInstall = createSelector(
  getFeatureFlags,
  flags => flags.enablePwaInstall || false
);

const getNewUserBannerFlag = createSelector(
  getFeatureFlags,
  flags => (flags.newUserBanner || 'OFF') === 'ON'
);
const getAppInstallFlag = createSelector(getFeatureFlags, flags => flags.appInstallFeature || {});
const getAppConfig = createSelector(getFeatureFlags, flags => flags.appConfig || {});

export const getTopShopperHeader = createSelector(
  getFeatureFlags,
  flags => flags.topShopperHeader || defaultFlags.flags.topShopperHeader
);

export const getTopShopperSubHeader = createSelector(
  getFeatureFlags,
  flags => flags.topShopperSubHeader || defaultFlags.flags.topShopperSubHeader
);

export const getEventHowToParticipateContentList = createSelector(
  getFeatureFlags,
  flags =>
    flags.eventHowToParticipateContentList || defaultFlags.flags.eventHowToParticipateContentList
);

export const getTopShopperGrand = createSelector(
  getFeatureFlags,
  flags => flags.topShopperGrand || defaultFlags.flags.topShopperGrand
);

export const getTopHeaderPrize = createSelector(
  getFeatureFlags,
  flags => flags.topHeaderPrize || defaultFlags.flags.topHeaderPrize
);

export const getLuckyDrawText = createSelector(
  getFeatureFlags,
  flags => flags.luckyDrawText || defaultFlags.flags.luckyDrawText
);

export const getLuckyChampList = createSelector(
  getFeatureFlags,
  flags => flags.luckyChampList || defaultFlags.flags.luckyChampList
);

export const getTopShopperHowToWinList = createSelector(
  getFeatureFlags,
  flags => flags.topShopperHowToWinList || defaultFlags.flags.topShopperHowToWinList
);

export const getCancellationRequest = createSelector(
  getFeatureFlags,
  flags => flags.enable_cancellation_request || defaultFlags.flags.enable_cancellation_request
);

export const getCancellationRequestOldFlag = createSelector(
  getFeatureFlags,
  flags => flags.cancelationRequestOldFlag || defaultFlags.flags.cancelationRequestOldFlag
);

export const getEnableRatingFlag = createSelector(
  getFeatureFlags,
  flags => flags.enableRating || defaultFlags.flags.enableRating
);

export const getSubratingFlag = createSelector(
  getFeatureFlags,
  flags => flags?.subRatingFlag || defaultFlags?.flags?.subRatingFlag
);

export const getOlRatingFlag = createSelector(
  getFeatureFlags,
  flags => flags.olRatingFlag || defaultFlags.flags.olRatingFlag
);

export const getOdRatingFlag = createSelector(
  getFeatureFlags,
  flags => flags.odRatingFlag || defaultFlags.flags.odRatingFlag
);

export const getFraudReductionForEmployeeCouponsFlag = createSelector(
  getFeatureFlags,
  flags =>
    (flags?.AjioFraudReductionForEmployeeCoupons?.fraudReductionForEmployeeCoupons || 'OFF') ===
    'ON'
  // flags => true
);

export const getFraudReductionForEmpCouponsLabels = createSelector(
  getFeatureFlags,
  flags => flags?.AjioFraudReductionForEmployeeCoupons?.ctaLabels || {}
);

export const getAvailableDomainsFlag = createSelector(
  getFeatureFlags,
  flags =>
    flags?.AjioFraudReductionForEmployeeCoupons?.availableDomains ||
    defaultFlags?.flags?.AjioFraudReductionForEmployeeCoupons?.availableDomains
);

const defaultUspsPayment = getValue(defaultFlags, 'flags.uspsPayment');
const getUspsPayment = createSelector(getFeatureFlags, flags => {
  const usps = flags?.uspsPayment || defaultUspsPayment;
  return (
    usps.reduce((acc, usp) => {
      if (acc) {
        if (usp) {
          return `${acc} | ${usp}`;
        }
      } else if (usp) {
        return `${usp}`;
      }
      return acc;
    }) || ''
  );
});

const defaultMsg = getValue(defaultFlags, 'flags.paymentMsg');
const getPaymentMsg = createSelector(getFeatureFlags, flags => flags?.paymentMsg || defaultMsg);

const defaultcodCustomMessage = getValue(defaultFlags, 'flags.codCustomMessage');
const getCodMessage = createSelector(
  getFeatureFlags,
  flags => flags?.codCustomMessage || defaultcodCustomMessage
);

const defaultshowStorePickUpTab = getValue(defaultFlags, 'flags.showStorePickUpTab');
const getShowStorePickUpTab = createSelector(
  getFeatureFlags,
  flags => flags?.showStorePickUpTab || defaultshowStorePickUpTab
);

const defaultPickFromStoreFlag = getValue(defaultFlags, 'flags.pickFromStoreFlag');
const getPickFromStoreFlag = createSelector(
  getFeatureFlags,
  flags => flags?.pickFromStoreFlag || defaultPickFromStoreFlag
);

export const getSimilarPopupTimeout = createSelector(
  getFeatureFlags,
  flags => flags?.similarPopupTimeOut || 2
);

const getNewBrandDirectoryMsiteFlag = createSelector(
  getFeatureFlags,
  flags => (flags?.isNewBrandDirectoryMsite || 'OFF') === 'ON'
);

const getNewBrandDirectoryDesktopFlag = createSelector(
  getFeatureFlags,
  flags => (flags?.isNewBrandDirectoryDesktop || 'OFF') === 'ON'
);
export const getSimilarPopupFlag = createSelector(
  getFeatureFlags,
  flags => (flags?.isSimilarPopupDisplay || 'OFF') === 'ON'
);

const getAssuredGifts = createSelector(
  getFeatureFlags,
  flags => (flags?.assuredGifts || 'OFF') === 'ON'
);

const getAssuredFlags = createSelector(getFeatureFlags, flags => {
  return flags.assuredGiftsLandingData || {};
});

// const getAssuredGiftsFlag = createSelector(getFeatureFlags, flags => flags.assuredGiftsFlag || {});
const getAssuredGiftsFlag = createSelector(getFeatureFlags, flags => {
  return flags.assuredGiftsFlag || {};
});


const getBankOffersPDPAggregator = state => {
  const { flags = {} } = state || {};
  const { bankOfferPDPAggregator = 'OFF' } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const bankofferpdpaggregator = getAmplitudeVariantPayload(
      'bankofferpdpaggregator',
      state,
      bankOfferPDPAggregator
    );
    return bankofferpdpaggregator === 'ON';
  }
  return (flags?.bankOfferPDPAggregator || 'OFF') === 'ON';
};

const getBankOffersHybris = state => {
  const { flags = {} } = state || {};
  const { bankOfferHybris = 'OFF' } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const bankofferhybris = getAmplitudeVariantPayload(
      'bankofferhybris',
      state,
      bankOfferHybris
    );
    return bankofferhybris === 'ON';
  }
  return (flags?.bankOfferHybris || 'OFF') === 'ON';
};

const getCodConvenienceData = createSelector(
  getFeatureFlags,
  flags => flags?.codConvenienceData || {}
);

const getEarlyAccessVariant = createSelector(getFeatureFlags, flags =>
  flags?.earlyAccessEnabled === 'ON' ? flags?.earlyAccessVariant || '' : ''
);

const getAjioWallet = createSelector(
  getFeatureFlags,
  flags => (flags?.displayAjioWallet || 'OFF') === 'ON'
);

const getUaas = createSelector(getFeatureFlags, flags => (flags?.isUaas || 'OFF') === 'ON');

const getSearchPageVersion = createSelector(getFeatureFlags, flags => flags?.searchPageVersion);

const getSearchPageVersionLuxe = createSelector(
  getFeatureFlags,
  flags => flags?.searchPageVersionLuxe
);

const getBrandCompare = createSelector(
  getFeatureFlags,
  flags => (flags?.brandCompare || 'OFF') === 'ON'
);

const getsizeChartSizeClick = createSelector(
  getFeatureFlags,
  flags => (flags?.sizeChartSizeClick || 'OFF') === 'ON'
);

const getBrickCodeList = createSelector(
  getFeatureFlags,
  flags => flags?.comprehensive_brickcode_list?.brickCode || []
);

const getSingleRecommend = createSelector(
  getFeatureFlags,
  flags => (flags?.singleSizeRecommend || 'OFF') === 'ON'
);

const getDeleteForNotifyMeFlag = createSelector(
  getFeatureFlags,
  flags => (flags?.enabledDeleteForNotifyMe || 'OFF') === 'ON'
);

const defaultNotifyMessageConfig = getValue(defaultFlags, 'flags.notifyMessageConfig');
const getNotifyMessageConfig = createSelector(
  getFeatureFlags,
  flags => flags?.notifyMessageConfig || defaultNotifyMessageConfig
);

const defaultBrandSizeToggleText = getValue(defaultFlags, 'flags.brandSizeToggleText');
const getBrandSizeToggleText = createSelector(
  getFeatureFlags,
  flags => flags?.brandSizeToggleText || defaultBrandSizeToggleText
);

const getBrandSizeToggleBtn = createSelector(
  getFeatureFlags,
  flags => (flags?.enableBrandSizeToggleBtn || 'OFF') === 'ON'
);
const getVersionData = createSelector(getFeatureFlags, flags => flags?.version || '');

const defaultFreeShippingAmountLimit = getValue(defaultFlags, 'flags.freeShippingAmountLimit');
const getFreeShippingAmountLimit = createSelector(
  getFeatureFlags,
  flags => flags?.freeShippingAmountLimit || defaultFreeShippingAmountLimit
);

const getFreeDeliveryPdpTagFlag = createSelector(
  getFeatureFlags,
  flags => (flags?.enabledFreeDeliveryPdpTag || 'OFF') === 'ON'
);

const getProductImageInColourPalletFlag = createSelector(
  getFeatureFlags,
  flags => (flags?.enabledProductImageInColourPallet || 'OFF') === 'ON'
);

const getAllOffers = state => {
  const { flags = {} } = state || {};
  const { allOffers = 'OFF' } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const allOffersVariant = getAmplitudeVariantPayload('allOffers', state, allOffers);
    return allOffersVariant === 'ON';
  }
  return (flags?.allOffers || 'OFF') === 'ON';
};

const getDataNotFoundText = createSelector(getFeatureFlags, flags => flags?.dataNotFoundText || '');
const defaultProductCountWishlistWidgetSearchScreen = getValue(
  defaultFlags,
  'flags.productCountWishlistWidgetSearchScreen'
);
const getProductCountWishlistWidgetSearchScreen = createSelector(
  getFeatureFlags,
  flags =>
    flags?.productCountWishlistWidgetSearchScreen || defaultProductCountWishlistWidgetSearchScreen
);

const getShowSimilarProductsForPlp = flags => {
  return (flags?.showSimilarProductsPlp || 'OFF') === 'ON';
};

const defaultNewOfferBenefitsConfig = getValue(defaultFlags, 'flags.newOfferBenefitsConfig');
const getNewOfferBenefitsConfig = createSelector(
  getFeatureFlags,
  flags => flags?.newOfferBenefitsConfig || defaultNewOfferBenefitsConfig
);

const getShowHomeRating = flags => {
  return (flags?.showHomeRating || 'OFF') === 'ON';
};

const getShowPDPRateSection = flags => {
  return (flags?.showPDPRateSection || 'OFF') === 'ON';
};

const getShowRateProdBtn = flags => {
  return (flags?.showRateProdBtn || 'OFF') === 'ON';
};

const getShowReviewProdBtn = flags => {
  return (flags?.showReviewProdBtn || 'OFF') === 'ON';
};

const getEnableRating = (flags, state) => {
  const currentStore = getCurrentStore(state);

  if (currentStore === FLEEK_STORE) return (flags?.enableAjiogramRating || 'OFF') === 'ON';
  if (currentStore === LUXE_STORE) return false;
  return (flags?.enableRating || 'OFF') === 'ON';
};
const getBannerLoginPageFlag = flags => {
  return (flags?.bannerLoginPageFlag || 'OFF') === 'ON';
};
const getBannerSignupPageFlag = flags => {
  return (flags?.bannerSignupPageFlag || 'OFF') === 'ON';
};

const defaultViewTypeFlag = getValue(defaultFlags, 'flags.showViewType');
const getViewTypeFlag = flags => {
  return flags?.showViewType || defaultViewTypeFlag;
};

// const getInternalWalletFlag = createSelector(
//   getFeatureFlags,
//   flags => (flags?.internalWalletsOnCart || 'OFF') === 'ON'
//   // flags => true
// );
const getInternalWalletFlag = state => {
  const { flags = {} } = state || {};
  const { internalWalletsOnCart = 'OFF' } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const internalWalletsOnCartFlag = getAmplitudeVariantPayload(
      'internalWalletsOnCart',
      state,
      internalWalletsOnCart
    );
    return internalWalletsOnCartFlag === 'ON';
  }
  return (flags?.internalWalletsOnCart || 'OFF') === 'ON';
};

const defaultHowToMeasureVisibleInSizeGuideFlag = () =>
  getValue(defaultFlags, 'flags.howToMeasureVisibleInSizeGuideFlag');

const getHowToMeasureVisibleInSizeGuideFlag = createSelector(
  getFeatureFlags,
  defaultHowToMeasureVisibleInSizeGuideFlag,
  (flags, defaultValue) => (flags?.howToMeasureVisibleInSizeGuideFlag || defaultValue) === 'ON'
);

const defaultHowToMeasureNewImagesFlag = () =>
  getValue(defaultFlags, 'flags.howToMeasureSizeGuide.howToMeasureVisibleInSizeGuideFlag');
const getHowToMeasureNewImagesFlag = createSelector(
  getFeatureFlags,
  defaultHowToMeasureNewImagesFlag,
  (flags, defaultValue) =>
    (flags?.howToMeasureSizeGuide?.howToMeasureNewImagesFlag || defaultValue) === 'ON'
);

const getHowToMeasureNewImagesProducts = createSelector(
  getFeatureFlags,
  flags => flags?.howToMeasureSizeGuide?.howToMeasureProducts || []
);

const getKnowTheProductBrickCodeList = createSelector(
  getFeatureFlags,
  flags => flags?.knowTheProduct_brickcode_list?.brickCode || []
);

const defaultBannerLoginPage = getValue(defaultFlags, 'flags.bannerLoginPage');
const getBannerLoginPage = createSelector(
  getFeatureFlags,
  flags => flags?.bannerLoginPage || defaultBannerLoginPage
);

const defaultBannerSignupPage = getValue(defaultFlags, 'flags.bannerSignupPage');
const getBannerSignupPage = createSelector(
  getFeatureFlags,
  flags => flags?.bannerSignupPage || defaultBannerSignupPage
);

const getEnableReturnBankInfoEncryption = flags => {
  return (flags?.enable_return_bank_info_encryption || 'OFF') === 'ON';
};

const getEnableSaveImpsBankEncryption = flags => {
  return (flags?.enable_save_imps_bank_encryption || 'OFF') === 'ON';
};

const defaultPublicKey = getValue(defaultFlags, 'flags.publicKey');
const getPublicKey = createSelector(getFeatureFlags, flags => flags?.publicKey || defaultPublicKey);

const defaultVersionForEncryption = getValue(defaultFlags, 'flags.versionForEncryption');
const getVersionForEncryption = createSelector(
  getFeatureFlags,
  flags => flags?.versionForEncryption || defaultVersionForEncryption
);

const getShowRateCalculate = flags => {
  return (flags?.showRateCalculate || 'OFF') === 'ON';
};

const defaultRateCalculateText = getValue(defaultFlags, 'flags.rateCalculateText');
const getRateCalculateText = createSelector(
  getFeatureFlags,
  flags => flags?.rateCalculateText || defaultRateCalculateText
);
const getBagChangeInterval = createSelector(
  getFeatureFlags,
  flags => flags?.bagChangesInterval || 1000
);

const defaultLoginToApplyCouponCTA = getValue(defaultFlags, 'flags.loginToApplyCouponCTA');
const getLoginToApplyCouponCTA = createSelector(
  getFeatureFlags,
  flags => flags?.loginToApplyCouponCTA || defaultLoginToApplyCouponCTA
);

const defaultLoggedInApplyCouponToast = getValue(defaultFlags, 'flags.loggedInApplyCouponToast');
const getLoggedInApplyCouponToast = createSelector(
  getFeatureFlags,
  flags => flags?.loggedInApplyCouponToast || defaultLoggedInApplyCouponToast
);

const getLuxeBrandPage = createSelector(
  getFeatureFlags,
  flags => (flags?.luxeBrandsPageWithImages || 'OFF') === 'ON'
);

export const getDressToolAtcPopupConfig = createSelector(
  getFeatureFlags,
  flags => flags?.dressToolAtcPopupConfig
);
export const getDressToolPdpBannerConfig = createSelector(
  getFeatureFlags,
  flags => flags?.dressToolPdpBannerConfig
);

const defaultUnlockedText = getValue(defaultFlags, 'flags.unlockedText');
const getUnlockedText = createSelector(
  getFeatureFlags,
  flags => flags?.unlockedText || defaultUnlockedText
);

const defaultFloatingWidgetFlags = getValue(defaultFlags, 'flags.floatingWidgetFlags');
const getFloatingWidgetFlags = createSelector(
  getFeatureFlags,
  flags => flags?.floatingWidgetFlags || defaultFloatingWidgetFlags
);
// msite firebase selectors
const getjioAdStartPositionMSite = state => {
  const { flags = {} } = state || {};
  const { jioAdStartPositionMSite = 0 } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const jioadstartpositionmsite = getAmplitudeVariantPayload(
      'jioadstartpositionmsite',
      state,
      jioAdStartPositionMSite
    );
    return jioadstartpositionmsite;
  }
  return flags?.jioAdStartPositionMSite || 0;
};

const getjioAdsEndPositionMsite = state => {
  const { flags = {} } = state || {};
  const { jioAdsEndPositionMsite = 0 } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const jioadsendpositionmsite = getAmplitudeVariantPayload(
      'jioadsendpositionmsite',
      state,
      jioAdsEndPositionMsite
    );
    return jioadsendpositionmsite;
  }
  return flags?.jioAdsEndPositionMsite || 0;
};

const getjioAdsFrequencyMsite = state => {
  const { flags = {} } = state || {};
  const { jioAdsFrequencyMsite = 0 } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const jioadsfrequencymsite = getAmplitudeVariantPayload(
      'jioadsfrequencymsite',
      state,
      jioAdsFrequencyMsite
    );
    return jioadsfrequencymsite;
  }
  return flags?.jioAdsFrequencyMsite || 0;
};

// desktop firebase selectors

const getjioAdsStartRowDesktop = state => {
  const { flags = {} } = state || {};
  const { jioAdsStartRowDesktop = 0 } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const jioadsstartrowdesktop = getAmplitudeVariantPayload(
      'jioadsstartrowdesktop',
      state,
      jioAdsStartRowDesktop
    );
    return jioadsstartrowdesktop;
  }
  return flags?.jioAdsStartRowDesktop || 0;
};

const getjioAdsEndRowDesktop = state => {
  const { flags = {} } = state || {};
  const { jioAdsEndRowDesktop = 0 } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const jioadsendrowdesktop = getAmplitudeVariantPayload(
      'jioadsendrowdesktop',
      state,
      jioAdsEndRowDesktop
    );
    return jioadsendrowdesktop;
  }
  return flags?.jioAdsEndRowDesktop || 0;
};

const getjioAdsRowCount = state => {
  const { flags = {} } = state || {};
  const { jioAdsRowCount = 0 } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const jioadsrowcount = getAmplitudeVariantPayload('jioadsrowcount', state, jioAdsRowCount);
    return jioadsrowcount;
  }
  return flags?.jioAdsRowCount || 0;
};

const getShowAdTagBannerListingAds = state => {
  const { flags = {} } = state || {};
  const { showAdTagBannerListingAds = false } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const showadtagbannerlistingads = getAmplitudeVariantPayload(
      'showadtagbannerlistingads',
      state,
      showAdTagBannerListingAds
    );
    return showadtagbannerlistingads;
  }
  return flags?.showAdTagBannerListingAds || false;
};

const defaultWishlistUnratedProductsWidgetPosition = getValue(
  defaultFlags,
  'flags.wishlistUnratedProductsWidgetPosition'
);
const getWishlistUnratedProductsWidgetPosition = createSelector(
  getFeatureFlags,
  flags =>
    flags?.wishlistUnratedProductsWidgetPosition || defaultWishlistUnratedProductsWidgetPosition
);

const defaultPLPUnratedProductsPosition = getValue(
  defaultFlags,
  'flags.plpUnratedProductsWigetPosition'
);

const getPLPUnratedProductsWigetPosition = createSelector(
  getFeatureFlags,
  flags => flags?.plpUnratedProductsWigetPosition || defaultPLPUnratedProductsPosition
);
const defaultBusinessCancellationMessage = getValue(
  defaultFlags,
  'flags.businessCancellationMessage'
);
const getBusinessCancellationMessage = createSelector(
  getFeatureFlags,
  flags => flags?.businessCancellationMessage || defaultBusinessCancellationMessage
);

const defaultRatingLabelDisplayPLP = getValue(defaultFlags, 'flags.displayRatingLabelPLP');
const getEnableRatingLabelPLP = createSelector(
  getFeatureFlags,
  flags => flags?.displayRatingLabelPLP || defaultRatingLabelDisplayPLP
);

const defaultRatingLabelDisplayWishlist = getValue(
  defaultFlags,
  'flags.displayRatingLabelWishlist'
);
const getEnableRatingLabelWishlist = createSelector(
  getFeatureFlags,
  flags => flags?.displayRatingLabelWishlist || defaultRatingLabelDisplayWishlist
);

const defaultRatingLabelDisplayCart = getValue(defaultFlags, 'flags.displayRatingLabelCart');
const getEnableRatingLabelCart = createSelector(
  getFeatureFlags,
  flags => flags?.displayRatingLabelCart || defaultRatingLabelDisplayCart
);

const defaultTncListData = getValue(defaultFlags, 'flags.tncListData');
const getTncListData = createSelector(
  getFeatureFlags,
  flags => flags?.tncListData || defaultTncListData
);

const getAjioMassEmailIdOptionalMsite = flags => {
  return flags?.streetFlags?.isEmailOptionalMsite === 'ON';
};

const getStreetFlags = createSelector(
  getFeatureFlags,
  flags =>
    flags?.streetFlags || {
      isThemeEnableMsite: 'OFF',
      isThemeEnableWeb: 'OFF',
      isEmailOptionalMsite: 'OFF',
      isEmailOptionalWeb: 'OFF'
    }
);

const getAjioStreetThemeFlags = (state, platform) => {
  const { isThemeEnableMsite, isThemeEnableWeb } = getStreetFlags(state);
  if (platform === '') {
    const isMobileDevice = isMobile();
    if (isMobileDevice) {
      platform = 'MSITE';
    } else {
      platform = 'WEB';
    }
  }
  const currentStore = getCurrentStore(state);
  let flagValue = false;
  if (currentStore.toLowerCase() === 'street') {
    if (platform === 'WEB') {
      if (isThemeEnableWeb === 'ON') flagValue = true;
    } else if (isThemeEnableMsite === 'ON') flagValue = true;
  }
  return flagValue;
};
const defaultStoresForHidingMRP = getValue(defaultFlags, 'flags.storesForHidingMRP');
const getStoresForHidingMRP = createSelector(
  getFeatureFlags,
  flags => flags?.storesForHidingMRP || defaultStoresForHidingMRP
);

const getSimilarProductsFlagsPDP = createSelector(
  getFeatureFlags,
  flags =>
    (flags && flags?.similarProductsPDP) || {
      stores: {
        isAjio: 'ON',
        isLuxe: 'ON',
        isStreet: 'ON',
        others: 'ON'
      }
    }
);

const getSimilarProductsFlagsPLP = createSelector(
  getFeatureFlags,
  flags =>
    (flags && flags?.similarProductsPDP) || {
      stores: {
        isAjio: 'ON',
        isLuxe: 'ON',
        isStreet: 'ON',
        others: 'ON'
      }
    }
);

const getShowSimilarProducts = (state, platform = '', pageType = 'PDP') => {
  const currentStore = getCurrentStore(state).toLowerCase();
  // console.log("TRACK SIMILAR PRODUCTS FLAG - getShowSimilarProducts ~ currentStore:", currentStore)
  const flagData =
    pageType === 'PDP' ? getSimilarProductsFlagsPDP(state) : getSimilarProductsFlagsPLP(state);
  // console.log("TRACK SIMILAR PRODUCTS FLAG - getShowSimilarProducts ~ flagData:", flagData)
  const { stores: { isAjio = '', isLuxe = '', isStreet = '', others = '' } = {} } = flagData;
  let flagValue = false;
  if ((currentStore === 'ajio' || currentStore === '') && isAjio === 'ON') {
    flagValue = true;
  } else if (currentStore === 'luxe' && isLuxe === 'ON') {
    flagValue = true;
  } else if (currentStore === 'street' && isStreet === 'ON') {
    const isAjioStreetFlags = getAjioStreetThemeFlags(state, platform);
    if (isAjioStreetFlags) flagValue = true;
  } else if (others === 'ON') {
    flagValue = true;
  }
  // console.log("TRACK SIMILAR PRODUCTS FLAG - getShowSimilarProducts ~ flagValue:", flagValue)
  return flagValue;
};

const getSimilarProductsMaxLimit = createSelector(
  getFeatureFlags,
  flags => flags?.similarProductsMaxLimit || 16
);

const getBagTotalNotAvailableForOrders = createSelector(
  getFeatureFlags,
  flags => flags?.bagTotalNotAvailableForOrders || {}
);

const getExchangeRTOScreensUpdate = createSelector(
  getFeatureFlags,
  flags => flags?.exchangeRTOScreenUpdate === 'ON'
);

const defaultReviewEnableFlag = getValue(defaultFlags, 'flags.enableReview');

const getEnableReviewFlag = createSelector(
  getFeatureFlags,
  flags => flags.enableReview || defaultReviewEnableFlag
);

const defaultPrivacyPolicy = getValue(defaultFlags, 'flags.privacyPolicyUrl');
const getPrivacyPolicy = createSelector(
  getFeatureFlags,
  flags => flags?.privacyPolicyUrl || defaultPrivacyPolicy
);

const defaultFileSize = getValue(defaultFlags, 'flags.enableReview');

const getFileSize = createSelector(
  getFeatureFlags,
  flags => flags?.fileSizeForCompression || defaultFileSize
);

const defaultShowUserConsent = getValue(defaultFlags, 'flags.showUserConsent');

const getUserConsent = createSelector(
  getFeatureFlags,
  flags => flags?.showUserConsent || defaultShowUserConsent
);

const defaultMasterFlagFleek = getValue(defaultFlags, 'flags.masterFlagFleek');
const defaultFleekFeedPaginationSize = getValue(defaultFlags, 'flags.fleekPaginationSize');

const getMasterFlagFleek = createSelector(getFeatureFlags, flags => 'ON');

const getDisableDragFlag = flags => {
  return flags?.disableFloatingWidgetDrag === 'ON';
};
const getFleekFeedPaginationSize = createSelector(
  getFeatureFlags,
  flags => flags.fleekFeedPaginationSize || defaultFleekFeedPaginationSize
);

const defaultMasterGA3Flag = getValue(defaultFlags, 'flags.master_GA3');

const getMasterGA3Flag = flags => {
  return flags?.master_GA3 || defaultMasterGA3Flag;
};

const defaultMasterGA4Flag = getValue(defaultFlags, 'flags.master_GA4');
const getMasterGA4Flag = flags => {
  return flags?.master_GA4 || defaultMasterGA4Flag;
};

const defaultReleaseGA4Flag = getValue(defaultFlags, 'flags.release1_GA4');
const getReleaseGA4Flag = flags => {
  return flags?.release1_GA4 || defaultReleaseGA4Flag;
};

const defaultShowClosedtoOpenBox = getValue(defaultFlags, 'flags.showClosedtoOpenBox');

const getShowClosedtoOpenBox = createSelector(
  getFeatureFlags,
  flags => flags?.showClosedtoOpenBox || defaultShowClosedtoOpenBox
);

const getSearchFlags = createSelector(getFeatureFlags, flags => {
  const { searchExpFlags = [] } = flags;
  let filterSearchFlag = [];

  if (searchExpFlags?.length > 0) {
    filterSearchFlag = searchExpFlags?.filter(
      flag => getFeatureVariant(Object.keys(flag)[0]) === '1'
    );
  }
  return filterSearchFlag;
});

const defaultTradeDiscountValueExpFlag = getValue(defaultFlags, 'flags.tradeDiscountValueExp');

const getTradeDiscountValueExpFlag = createSelector(
  getFeatureFlags,
  flags => flags.tradeDiscountValueExp || defaultTradeDiscountValueExpFlag
);

const defaultTradeDiscountPercentageMax = getValue(
  defaultFlags,
  'flags.tradeDiscountPercentageMax'
);

const getTradeDiscountPercentageMax = createSelector(
  getFeatureFlags,
  flags => flags.tradeDiscountPercentageMax || defaultTradeDiscountPercentageMax
);

const defaultTradeDiscountValueMin = getValue(defaultFlags, 'flags.tradeDiscountValueMin');

const getTradeDiscountValueMin = createSelector(
  getFeatureFlags,
  flags => flags.tradeDiscountValueMin || defaultTradeDiscountValueMin
);
const getEnableProductCodeTag = flags => {
  return (flags?.enableProductCodeTag || 'ON') === 'OFF';
};
const defaultShowRevampedNavigationBar = getValue(defaultFlags, 'flags.showRevampedNavigation');

const getShowRevampedNavigation = createSelector(
  getFeatureFlags,
  flags => flags?.showRevampedNavigation || defaultShowRevampedNavigationBar
);

const defaultShowInterstitial = getValue(defaultFlags, 'flags.showInterstitial');

const getShowInterstitial = createSelector(
  getFeatureFlags,
  flags => flags?.showInterstitial || defaultShowInterstitial
);
const defaultInterstitialMilestonePeriod = getValue(
  defaultFlags,
  'flags.interstitialMilestonePeriod'
);

const getInterstitialMilestonePeriod = createSelector(
  getFeatureFlags,
  flags => flags?.interstitialMilestonePeriod || defaultInterstitialMilestonePeriod
);
const defaultShowCoachMark = getValue(defaultFlags, 'flags.showCoachMark');
const getSearchBarAnimationCounter = createSelector(
  getFeatureFlags,
  flags => flags?.searchBarAnimationCounter || defaultSearchBarAnimationCounter
);
const defaultshowConvertedGifs = getValue(defaultFlags, 'flags.showConvertedGifs');
const getShowConvertedGifs = createSelector(
  getFeatureFlags,
  flags => flags?.showConvertedGifs || defaultshowConvertedGifs
);
const defaultSearchBarAnimationCounter = getValue(defaultFlags, 'flags.searchBarAnimationCounter');
const getShowCoachMark = createSelector(
  getFeatureFlags,
  flags => flags?.showCoachMark || defaultShowCoachMark
);
const defaultNavigationConfigs = getValue(defaultFlags, 'flags.navigationConfigs');
const getNavigationConfigs = createSelector(
  getFeatureFlags,
  flags => flags?.navigationConfigs || defaultNavigationConfigs
);

const getEnableDeliveryFeeTag = flags => {
  return flags?.enableDeliveryFeeTag === 'ON';
};
const getStoreWiseDeliveryFeeTags = flags => {
  return flags?.storeWiseDeliveryFeeTags || {};
};

const defaultFleekVideoControlConfig = getValue(defaultFlags, 'flags.fleekVideoControlConfig');
const getFleekVideoControlConfig = flags => {
  return flags?.fleekVideoControlConfig || defaultFleekVideoControlConfig;
};
const defaultShowFleekSplashScreen = getValue(defaultFlags, 'flags.showFleekSplashScreen');

const getShowFleekSplashScreen = createSelector(
  getFeatureFlags,
  flags => flags?.showFleekSplashScreen || defaultShowFleekSplashScreen
);
const defaultOnboardingImages = getValue(defaultFlags, 'flags.onboardingImages');
const getOnboardingImages = createSelector(
  getFeatureFlags,
  flags => flags?.onboardingImages || defaultOnboardingImages
);

const defaultBackgroundOnboardingImage = getValue(defaultFlags, 'flags.onBoardingBackgroundImage');
const getBackgroundOnboardingImage = createSelector(
  getFeatureFlags,
  flags => flags?.onBoardingBackgroundImage || defaultBackgroundOnboardingImage
);

const defaultOnBoardingFleekAutoScroll = getValue(defaultFlags, 'flags.onBoardingFleekAutoScroll');
const getOnBoardingFleekAutoScroll = createSelector(
  getFeatureFlags,
  flags => flags?.onBoardingFleekAutoScroll || defaultOnBoardingFleekAutoScroll
);
const defaultShowFleekOnboardingScreens = getValue(
  defaultFlags,
  'flags.showFleekOnboardingScreens'
);

const getShowFleekOnboardingScreens = createSelector(
  getFeatureFlags,
  flags => flags?.showFleekOnboardingScreens || defaultShowFleekOnboardingScreens
);

const defaultFleekMiniPLPProductCount = getValue(defaultFlags, 'flags.fleekMiniPLPProductCount');

const getFleekMiniPLPProductCount = createSelector(
  getFeatureFlags,
  flags => flags?.fleekMiniPLPProductCount || defaultFleekMiniPLPProductCount
);

const defaultOnBoardingAutoScrollDuration = getValue(
  defaultFlags,
  'flags.onBoardingAutoScrollDuration'
);
const getOnBoardingAutoScrollDuration = createSelector(
  getFeatureFlags,
  flags => flags?.onBoardingAutoScrollDuration || defaultOnBoardingAutoScrollDuration
);
const defaultChildStores = getValue(defaultFlags, 'flags.childStores');
const getChildStores = createSelector(
  getFeatureFlags,
  flags => flags?.childStores || defaultChildStores
);

const getRemovalLoginPasswordJson = flags => {
  return flags?.removal_login_password_json || {};
};

const getEnableRestorePhoneNumber = flags => {
  return flags?.removal_login_password_json?.enable_restore_phone_number || false; // at time of commit make it false
};

const getAccountCheckApiErrorMsgForSignup = flags => {
  return flags?.accountCheckApiErrorMsgForSignup || 'Email ID is already registered with us';
};

const getAffiseAfSecure = flags => {
  return flags?.affiseAfSecure || '';
};

const getEnableLinkCohort = flags => {
  return flags?.enableLinkCohort || 'ON';
};

const getAjiogramDesktopRedirectBanner = flags => {
  return flags?.ajiogramDesktopRedirectBanner || defaultFlags?.ajiogramDesktopRedirectBanner;
};

const getRetireRCSWallet = flags => {
  return flags?.retireRCSWallet || defaultFlags?.retireRCSWallet;
};

const getRetireRCSWalletLedger = flags => {
  return flags?.retireRCSWalletLedger || defaultFlags?.retireRCSWalletLedger;
};
const defaultRetireRCSWalletText = getValue(defaultFlags, 'flags.retireRCSWalletText');
const getRetireRCSWalletText = flags => {
  return flags?.retireRCSWalletText || defaultRetireRCSWalletText;
};

const getPdpUiConfiguration = (flags, state) => {
  const currentStore = getCurrentStore(state);
  if (currentStore === 'luxe') {
    if (flags?.luxe_pdp_ui_configuration && !_isEmpty(flags?.luxe_pdp_ui_configuration)) {
      return flags?.luxe_pdp_ui_configuration;
    }

    return {
      HEADER_MODEL: 1,
      PRODUCT_SCROLLER: 2,
      PRODUCT_INFO_MODEL: 3,
      EDD_MODEL: 5,
      BANK_OFFERS: 6,
      OFFER_MODEL: 7,
      PDP_DETAILS: 8, // ajio luxe
      SHOPPING_ASSISTANT_MODEL: 9,
      RETURN_MODEL: 10,
      PDP_ACTIONS: 11,
      RV_MODEL: 12,
      SIMILAR_MODEL_PRODUCT: 13,
      QUICK_LINKS: 14,
      FOOTER_MODEL: 15,
      PDP_MORE_INFORMATION: 16,
      PDP_KNOW_THE_PRODUCT: 17,
      RATING_AND_REVIEW_MODEL: 18,
      YOU_MAY_ALSO_LIKE_MODEL: 19,
      EXTERNAL_ADS_MODEL: 14
    };
  }
  if (currentStore === FLEEK_STORE) {
    if (flags?.ajiogram_pdp_ui_configuration && !_isEmpty(flags?.ajiogram_pdp_ui_configuration)) {
      return flags?.ajiogram_pdp_ui_configuration;
    }
    const ajiogram_pdp_ui_configuration = {
      HEADER_MODEL: 1,
      PRODUCT_SCROLLER: 2,
      PRODUCT_INFO_MODEL: 3,
      COLOR_MODEL: 4,
      SIZE_MODEL: 5,
      EDD_MODEL: 6,
      RETURN_MODEL: 7,
      OFFER_MODEL: 8,
      BANK_OFFERS: 9,
      PDP_MORE_INFORMATION: 10,
      PDP_KNOW_THE_PRODUCT: 11,
      STYLING_IDEA: 12,
      RATING_AND_REVIEW_MODEL: 13,
      BRAND_DESC_MODEL: 14,
      PDP_ACTIONS: 15,
      REFERRAL_MODEL: 16,
      QUICK_LINKS: 17,
      SIMILAR_MODEL_PRODUCT: 18,
      RV_MODEL: 19,
      FOOTER_MODEL: 20,
      ASSURED_QUALITY: 21,
      EXTERNAL_ADS_MODEL: 14
    };
    return ajiogram_pdp_ui_configuration;
  }
  if (flags?.ajio_pdp_ui_configuration && !_isEmpty(flags?.ajio_pdp_ui_configuration)) {
    return flags?.ajio_pdp_ui_configuration;
  }
  return {
    HEADER_MODEL: 1,
    PRODUCT_SCROLLER: 2,
    PRODUCT_INFO_MODEL: 3,
    ASSURED_QUALITY: 4,
    OFFER_MODEL: 5,
    METAL_PURITY: 6,
    PRICE_REVEAL: 7,
    SIZE_MODEL: 7,
    PRICE_BREAKUP_MODEL: 7,
    EDD_MODEL: 8,
    BANK_OFFERS: 9,
    COLOR_MODEL: 10,
    PDP_DETAILS: 11,
    TRUST_MARKER: 12,
    RATING_AND_REVIEW_MODEL: 13,
    RETURN_MODEL: 14,
    PDP_ACTIONS: 15,
    REFERRAL_MODEL: 16,
    QUICK_LINKS: 17,
    CROSS_SELL: 18,
    SIMILAR_MODEL_PRODUCT: 19,
    RV_MODEL: 20,
    FOOTER_MODEL: 21,
    PDP_MORE_INFORMATION: 22,
    PDP_KNOW_THE_PRODUCT: 23,
    YOU_MAY_ALSO_LIKE_MODEL: 24,
    PDP_INTERVENTIONS: 13,
    EXTERNAL_ADS_MODEL: 14
  };
};

const getLandingPageFloatingWidget = flags => {
  return (flags?.landingPageFloatingWidget || 'OFF') === 'ON';
};

const getEnableReturnProcessingFee = flags => {
  return flags?.enableReturnProcessingFee || 'OFF';
};

const getReturnProcessingFeeCartPageText = flags => {
  const defaultVal = defaultFlags.flags.returnProcessingFeeCartPageText;
  return flags?.returnProcessingFeeCartPageText || defaultVal;
};

const getBlockedForCancellationFlagDesktop = flags => {
  return (flags?.blockedForCancellationDesktop || 'OFF') === 'ON';
};

const getBlockedForCancellationFlagMsite = flags => {
  return (flags?.blockedForCancellationMsite || 'OFF') === 'ON';
};

const getRemoveAjioWalletRefundDesktop = flags => {
  // return true;
  return (flags?.removeAjioWalletRefundDesktop || 'OFF') === 'ON';
};

const getRemoveAjioWalletRefundMsite = flags => {
  // return true;
  return (flags?.removeAjioWalletRefundMsite || 'OFF') === 'ON';
};

const getUpiCodRefundFlag = state => {
  const { flags = {} } = state || {};
  return getAmplitudeVariantPayload('getUpiCodRefundFlag', state, flags?.getUpiCodRefundFlag) || false;
};

const getUpiCodRefundDesktopFlag = state => {
  const { flags = {} } = state || {};
  return (
    getAmplitudeVariantPayload('upiCodRefundDesktopFlag', state, flags?.upiCodRefundDesktopFlag) ||
    false
  );
};

const getUpiCodRefundConfig = (state = {}) => {
  const { flags = {} } = state || {};
  return getAmplitudeVariantPayload('getUpiCodRefundConfig', state, flags?.getUpiCodRefundConfig || defaultFlags?.flags?.getUpiCodRefundConfig) || {};
};

const getRemoveAjioWalletRefundTexts = flags => {
  // return {
  //   removeAjioWalletReturnText: 'Enter your Bank details to initiate refund.',
  //   removeAjioWalletReturnTextForSelfShip: 'Kindly Enter your Bank details to initiate refund.',
  //   orderListingBankDetailsRequired: 'Kindly enter your bank details inside.'
  // }
  return flags.removeAjioWalletRefundTexts || {}
};

const getGA4AJIOEnable = flags => {
  return (flags?.ga4_ajio_enable || defaultFlags?.flags?.ga4_ajio_enable) === 'ON';
}

const getEMIEnable = flags => {
  return (flags?.emi_enabled || defaultFlags?.flags?.emi_enabled) === 'ON';
}

const getAccountDeletionFlag = flags => {
  return (flags?.accountDeletionFeatureConfig?.isAccountDeletionEnable || defaultFlags?.flags?.accountDeletionFeatureConfig?.isAccountDeletionEnable) === 'ON';
}

const defaultAccountDeletionFeatureConfig = getValue(defaultFlags, 'flags.accountDeletionFeatureConfig');
const getAccountDeletionFeatureConfig = flags => {
  return flags?.accountDeletionFeatureConfig || defaultAccountDeletionFeatureConfig;
};


const defaultPDPVideosFeatureConfig = getValue(defaultFlags, 'flags.pdpVideosFeatureConfig');
const getPDPVideosFeatureConfig = state => {

  if (state?.flags.amplitude_experiment_flag === 'ON') {
    let PDPVideosFeatureConfig = getAmplitudeVariantPayload(
          'pdpvideosfeatureconfig',
          state,
          defaultPDPVideosFeatureConfig
        );

      return (
        PDPVideosFeatureConfig
      );
    }

  return defaultPDPVideosFeatureConfig;
}

const getPDPVideosFeatureEnable = state => {

  if (state?.flags.amplitude_experiment_flag === 'ON') {
    let PDPVideosFeatureConfig = getAmplitudeVariantPayload(
          'pdpvideosfeatureconfig',
          state,
          defaultPDPVideosFeatureConfig
        );

      return (PDPVideosFeatureConfig?.enableVideosOnPDP || defaultFlags?.flags?.pdpVideosFeatureConfig?.enableVideosOnPDP) === 'ON'
    }

  return false;
}

const getSocialLogin = flags => {
  return (flags?.social_login || defaultFlags?.flags?.social_login) === 'ON';
}

const defaultgetReturnCodCommunication = getValue(defaultFlags, 'flags.returnCodCommunication');
const getReturnCodCommunication = createSelector(
  getFeatureFlags,
  flags => flags?.returnCodCommunication || defaultgetReturnCodCommunication
);

const defaultonboarding3PVendorsText = getValue(defaultFlags, 'flags.onboarding3PVendorsText');
const getOnboarding3PVendorsText = createSelector(
  getFeatureFlags,
  flags => flags?.onboarding3PVendorsText || defaultonboarding3PVendorsText
);

const defaultPanCardConfig = getValue(defaultFlags, 'flags.enable_pancard_collection');
const getPanCardConfig = createSelector(
  getFeatureFlags,
  flags => flags?.enable_pancard_collection || defaultPanCardConfig
);

const getEnablePreOrderOnbording3pVendors = flags =>{
  return (flags?.enablePreOrderOnbording3pVendors || 'OFF') === 'ON';
}
const getEnableChatWithUsODP = flags => {
  console.log('enableChatWithUsODP', flags?.enableChatWithUsODP);
  return (flags?.enableChatWithUsODP || 'OFF') === 'ON'
};

const defaultColorSwatchBrickCode = getValue(defaultFlags, 'flags.colorSwatchBrickCode');
const getColorSwatchBrickCode = createSelector(
  getFeatureFlags,
  flags => flags?.colorSwatchBrickCode || defaultColorSwatchBrickCode
);

const getPdpToBagConversionMasterFlag = flags => {
  return (flags?.pdpToBagConversionMasterFlag || 'OFF') === 'ON';
};

const getPdpToBagConversionConfig = flags => {
  return flags?.pdpToBagConversionConfig || {};
};

const getLuxeBottomIconStripUrl = flags =>{
  return flags?.luxeBottomIconStripUrl || ''
}

const getGiftWithProductEnable = flags =>{
  // return true;
  return (flags?.enableGiftWithProduct || 'OFF') === 'ON';
}
const getDefaultPDPSkuSizeSelectionFlag = flags => {
  return (flags?.enableDefaultSizeSelection || 'OFF') === 'ON';
};

const defaultGiftWithProductConfig = getValue(defaultFlags, 'flags.giftWithProductConfig');
const getGiftWithProductConfig = createSelector(
  getFeatureFlags,
  flags => flags?.giftWithProductConfig || defaultGiftWithProductConfig
);

const defaultAssetConfig = getValue(defaultFlags, 'flags.assetConfig');
// const getAssetConfig = createSelector(
//   getFeatureFlags,
//   flags => flags?.assetConfig || defaultAssetConfig
// );

const getAssetConfig = flags => {
  return flags?.assetConfig || defaultAssetConfig
};

const defaultPriorityDeliveryConfig = getValue(defaultFlags, 'flags.priorityDeliveryConfig');
const defaultPriorityDeliverySLPTagsConfig = getValue(defaultFlags, 'flags.priorityDeliverySLPTagsConfig');
const defaultRushDeliverySLPTagsConfig = getValue(defaultFlags, 'flags.rushDeliverySLPTagsConfig');
const defaultPlpOnLandingConfig = getValue(defaultFlags, 'flags.plponlanding');

const defaultEnableRushDelivery = getValue(defaultFlags, 'flags.enableRushDelivery');
const defaultGoogleMapsApiKey = getValue(defaultFlags, 'flags.googleMapsApiKey');
const defaultRushAvailabilityCookieExpireTimeInHours = getValue(defaultFlags, 'flags.rushAvailabilityCookieExpireTimeInHours');
const defaultCoachMarkRushConfig = getValue(defaultFlags, 'flags.coachMarkRushConfig');

const defaultGwpCartFreeBiesText = getValue(defaultFlags, 'flags.gwpCartFreeBiesText');
const getGwpCartFreeBiesText = createSelector(
  getFeatureFlags,
  flags => flags?.gwpCartFreeBiesText || defaultGwpCartFreeBiesText
);
const defaultGwpCartRemoveCartLevel = getValue(defaultFlags, 'flags.gwpCartRemoveCartLevel');
const getGwpCartRemoveCartLevel = createSelector(
  getFeatureFlags,
  flags => flags?.gwpCartRemoveCartLevel || defaultGwpCartRemoveCartLevel
);
const defaultGwpCartRemoveProductLevel = getValue(defaultFlags, 'flags.gwpCartRemoveProductLevel');
const getGwpCartRemoveProductLevel = createSelector(
  getFeatureFlags,
  flags => flags?.gwpCartRemoveProductLevel || defaultGwpCartRemoveProductLevel
);

const defaultGwpConfig = getValue(defaultFlags, 'flags.giftWithProductConfig');

const getGwpConfig = createSelector(getFeatureFlags, flags => flags?.giftWithProductConfig || defaultGwpConfig)

// const getEnableGWPMasterFlag = createSelector(getFeatureFlags, flags => flags?.enableGiftWithProduct || 'OFF');

const getPriorityDeliveryPostOrderFlag = flags => {
  return (flags?.enablePriorityDeliveryPostOrder || 'OFF') === 'ON';
};

const getPriorityDeliveryPreOrderFlag = flags => {
  return (flags?.enablePriorityDeliveryPreOrder || 'OFF') === 'ON';
};

const getPriorityDeliveryConfig = state => {

  if (state?.flags.amplitude_experiment_flag === 'ON') {
      return (
        getAmplitudeVariantPayload(
          'prioritydeliveryconfig',
          state,
          state?.flags?.priorityDeliveryConfig
        ) || state?.flags?.priorityDeliveryConfig || defaultPriorityDeliveryConfig
      );
    }
  return state?.flags?.priorityDeliveryConfig || defaultPriorityDeliveryConfig;
}

const getPriorityDeliverySLPTagsConfig = state => {

  if (state?.flags.amplitude_experiment_flag === 'ON') {
      return (
        getAmplitudeVariantPayload(
          'prioritydeliveryslptagsconfig',
          state,
          state?.flags?.priorityDeliverySLPTagsConfig
        ) || state?.flags?.priorityDeliverySLPTagsConfig || defaultPriorityDeliverySLPTagsConfig
      );
    }

  return state?.flags?.priorityDeliverySLPTagsConfig || defaultPriorityDeliverySLPTagsConfig;
}


const getRushAvailabilityCookieExpireTimeInHours = state => {

  if (state?.flags?.amplitude_experiment_flag === 'ON') {
    let rushavailabilitycookieexpiretimeinhours = getAmplitudeVariantPayload(
          'rushavailabilitycookieexpiretimeinhours',
          state,
          0
        );
      return (
        rushavailabilitycookieexpiretimeinhours?.value || defaultRushAvailabilityCookieExpireTimeInHours || 0
      );
    }

  return defaultRushAvailabilityCookieExpireTimeInHours || 0;
}

const getRushCoachMarkConfig = state => {
  if (state?.flags.amplitude_experiment_flag === 'ON') {
      return (
        getAmplitudeVariantPayload(
          'coachmarkrushconfig',
          state,
          {}
        ) || defaultCoachMarkRushConfig || {}
      );
    }
  return defaultCoachMarkRushConfig || {};
}

const bank_discount_clawback_config = (state) => {
  if (state?.flags.amplitude_experiment_flag === 'ON') {
    return (
      getAmplitudeVariantPayload(
        'bankdiscountclawback',
        state,
        {}
      ) || {}
    );
  }
return {};
}

const getEnableEnchancedOosCart = flags => {
  return (flags?.enableEnchancedOosCart || 'OFF') === 'ON';
}

const getEnableGWPMasterFlag = createSelector(getFeatureFlags, flags => flags?.enableGiftWithProduct || 'OFF');

const getBarcodingPolyBagConfig = flags => {
  return flags?.barcodingPolyBagConfig || {}
};

const getRatingsColorConfig = flags => {
  return flags?.ratingThreshold || {};
};
const defaultAppsFlyerConfig = getValue(defaultFlags, 'flags.appsFlyerConfig');
const defaultAjiogramInterventionsConfig = getValue(defaultFlags, 'flags.ajiogramInterventionsConfig');

const getReturnProcessingFeePostOrderFlag = flags =>{
  // return true;
  return (flags?.enableReturnProcessingFeePostOrder || 'OFF') === 'ON';
};

const getReturnProcessingFeePostOrderConfig = state => {
  return (
    state?.ordersData?.orderLevelDetail?.exchangeNudge?.POST_ORDER_RETURN_FEE?.actionContent || {}
  );
}

const getStoreCredit = state => {
  return (
    state?.ordersData?.orderLevelDetail?.orderResponse?.orderSummary?.storeCredit?.value || ''
  );
}

const getEnableComingSoonProducts = flags =>{
  return (flags?.enableComingSoonProducts || 'OFF') === 'ON';
};

const getShowSelectedFiltersTab = ( flags ) => {
  return (flags?.showSelectedFiltersTab || 'OFF') === 'ON';
};

const getEnableOspreySearch = flags => {
  return (flags?.enableOspreySearch || 'OFF') === 'ON';
};

const getEnableVertexSearch = flags => {
  return (flags?.vertexEnabled || 'OFF') === 'ON';
};

const getBetterPdpConfigFlag = flags => {
  return (flags?.better_pdp_config?.isEnabled || 'OFF') === 'ON';
};

const getSectionOneTabularViewFlag = flags => {
  return (flags?.better_pdp_config?.isSectionOneTabularView || 'OFF') === 'ON';
};

const getSectionTwoTabularViewFlag = flags => {
  return (flags?.better_pdp_config?.isSectionTwoTabularView || 'OFF') === 'ON';
};

const getSectionThreeTabularViewFlag = flags => {
  return (flags?.better_pdp_config?.isSectionThreeTabularView || 'OFF') === 'ON';
};

const getMaxKeyValuePairFlag = flags => {
  return flags?.better_pdp_config?.max_key_value_pair;
};
const getMaxLineForValueFlag = flags => {
  return flags?.better_pdp_config?.max_line_for_value;
};
const getBetterPdpTitleFlag = flags => {
  return flags?.better_pdp_config?.better_pdp_title;
};

const getEnableCODCheck = flags => {
  return (flags?.enableCODCheck || 'OFF') === 'ON';
}
const getEnableAjioMemberShipProgram = (flags, state) => {
  const {enableAjioMemberShipProgram = 'OFF'} = flags
  if (flags?.amplitude_experiment_flag === 'ON') {
    const membershipFlag = getAmplitudeVariantPayload('enableajiomembershipprogram',state, enableAjioMemberShipProgram);
    console.log("membershipFlag", membershipFlag);
    return membershipFlag;
  }
  return (flags?.enableAjioMemberShipProgram || 'OFF') === 'ON';
}

const defaultSubscriptionBannerConfig = getValue(defaultFlags, 'flags.subscriptionBannerConfig');

const getSubscriptionBannerConfig = (flags, state) => {
  const { ajiomembershipconfig = {} } = flags;
  if (flags?.amplitude_experiment_flag === 'ON') {
    const membershipConfig = getAmplitudeVariantPayload(
      'ajiomembershipconfig',
      state,
      ajiomembershipconfig
    );
    console.log("membershipConfig", membershipConfig);
    return membershipConfig?.subscriptionBannerConfig;
  }
  return flags?.subscriptionBannerConfig || defaultSubscriptionBannerConfig;
}

const defaultAjioMemberShipConfig = getValue(defaultFlags, 'flags.ajioMemberShipConfig');

// Legacy function signature - used across many components
const getAjioMemberShipConfig = (flags, state = {}) => {
  const { ajiomembershipconfig = {} } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const membershipConfig = getAmplitudeVariantPayload(
      'ajiomembershipconfig',
      state,
      ajiomembershipconfig
    );
    return membershipConfig;
  }
  return flags?.ajioMemberShipConfig || defaultAjioMemberShipConfig;
}

// Memoized selector to prevent infinite re-renders in connected components
const getAjioMemberShipConfigMemoized = createSelector(
  [
    (state) => state?.flags?.amplitude_experiment_flag,
    (state) => state?.variants?.ajiomembershipconfig,
    (state) => state?.flags?.ajiomembershipconfig,
    (state) => state?.flags?.ajioMemberShipConfig
  ],
  (amplitudeFlag, variantData, flagAjioMembershipConfig, ajioMemberShipConfig) => {
    if (amplitudeFlag === 'ON' && variantData) {
      if (variantData.value === 'on') {
        return variantData.payload;
      }
      return flagAjioMembershipConfig || {};
    }
    return ajioMemberShipConfig || defaultAjioMemberShipConfig;
  }
);
const getEnableCrossSell = flags => {
  return (flags?.enableCrossSell || 'OFF') === 'ON';
}

const getNewReferralUI = (state) => {
  const { flags } = state;
  const { referallFriendConfig = {} } = flags || {};
  const referallConfig = getAmplitudeVariantPayload(
    'referallfriendconfig',
    state,
    referallFriendConfig
  );
  return referallConfig;
}

const defaultPromoteExchangeWebConfig = getValue(defaultFlags, 'flags.promoteExchangeWebConfig');
const defaultPromoteExchangeMsiteConfig = getValue(
  defaultFlags,
  'flags.promoteExchangeMsiteConfig'
);

const getPromoteExchangeWebConfig = state => {
  const { flags = {} } = state;
  const currentStore = getCurrentStore(state).toLowerCase();
  if (currentStore === 'ajio' || currentStore === '') {
    return flags.promoteExchangeWebConfig || defaultPromoteExchangeWebConfig;
  }
  return {};
};

const getPromoteExchangeMsiteConfig = state => {
  const { flags = {} } = state;
  const currentStore = getCurrentStore(state).toLowerCase();
  if (currentStore === 'ajio' || currentStore === '') {
    return flags.promoteExchangeMsiteConfig || defaultPromoteExchangeMsiteConfig;
  }
  return {};
};

const getAppsFlyerConfig = flags => {
  return flags?.appsFlyerConfig || defaultAppsFlyerConfig;
}

const getNewShowClosedBoxToOpenBox = state => {
  const { flags = {} } = state || {};

  return (() => {
    if (flags.amplitude_experiment_flag === 'ON') {
      return (
        getAmplitudeVariantPayload(
          'newshowclosedboxtoopenbox',
          state,
          flags?.newShowClosedBoxToOpenBox
        ) || false
      );
    }

    return (flags?.newShowClosedBoxToOpenBox || 'OFF') === 'ON';
  })();
};

const getEnableBestCouponOnCart = state => {
  const { flags = {} } = state || {};

  return (() => {
    if (flags.amplitude_experiment_flag === 'ON') {
      return (
        getAmplitudeVariantPayload(
          'enablebestcoupononcart',
          state,
          flags?.enableBestCouponOnCart
        ) || false
      );
    }

    return (flags?.enableBestCouponOnCart || 'OFF') === 'ON';
  })();
};

const getPriorityDeliveryPhase2Flag = flags => {
  return (
    (flags?.priorityDeliveryPhase2 || defaultFlags?.flags?.priorityDeliveryPhase2) === 'ON'
  );
};

const getRushDeliveryFlag = state => {
  if (state?.flags.amplitude_experiment_flag === 'ON') {
      return (
        getAmplitudeVariantPayload(
          'enableRushDelivery',
          state,
          false
        ) || false
      );
    }

  return false;
};

const getGoogleMapsKey = state => {

  if (state?.flags.amplitude_experiment_flag === 'ON') {
    let googlemapsapikey = getAmplitudeVariantPayload(
          'googlemapsapikey',
          state,
          ""
        );
      return (
        googlemapsapikey?.value || defaultGoogleMapsApiKey || ""
      );
    }

  return defaultGoogleMapsApiKey || "";
};

const getNoDeliveryIconStatuses = state => {
   if (state?.flags.amplitude_experiment_flag === 'ON') {
    return getAmplitudeVariantPayload('noDeliveryIconStatuses', state, []) || [];
  }
  return [];
};

const defaultCustomerCelebrationConfig = getValue(defaultFlags, 'flags.customer_celebration_config');
const getCustomerCelebrationConfig = state => {
  return getAmplitudeVariantPayload('customer_celebration_config', state, defaultCustomerCelebrationConfig) || defaultCustomerCelebrationConfig;
};

const getAjiogramInterventionsConfig = flags => {
  return flags?.ajiogramInterventionsConfig || defaultAjiogramInterventionsConfig
};

const getPaymentAndRefundCreditModesFlag = flags => {
  return (
    (flags?.paymentAndRefundCreditConfig?.showPaymentAndRefundCreditModes ||
      defaultFlags?.flags?.paymentAndRefundCreditConfig?.showPaymentAndRefundCreditModes) === 'ON'
  );
};

const getPaymentModesOnOrderPlacementFlag = flags => {
  const masterFlag = getPaymentAndRefundCreditModesFlag(flags);
  const showPaymentModesOnOrderPlacement =
    flags?.paymentAndRefundCreditConfig?.showPaymentModesOnOrderPlacement ||
    defaultFlags?.flags?.paymentAndRefundCreditConfig?.showPaymentModesOnOrderPlacement;

  return masterFlag && showPaymentModesOnOrderPlacement === 'ON';
};

const getRefundCreditModesOnReturnFlag = flags => {
  const masterFlag = getPaymentAndRefundCreditModesFlag(flags);
  const showRefundCreditModesOnReturn =
    flags?.paymentAndRefundCreditConfig?.showRefundCreditModesOnReturn ||
    defaultFlags?.flags?.paymentAndRefundCreditConfig?.showRefundCreditModesOnReturn;

  return masterFlag && showRefundCreditModesOnReturn === 'ON';
};

const getRefundCreditModesOnCancellationFlag = flags => {
  const masterFlag = getPaymentAndRefundCreditModesFlag(flags);
  const showRefundCreditModesOnCancellation =
    flags?.paymentAndRefundCreditConfig?.showRefundCreditModesOnCancellation ||
    defaultFlags?.flags?.paymentAndRefundCreditConfig?.showRefundCreditModesOnCancellation;

  return masterFlag && showRefundCreditModesOnCancellation === 'ON';
};

const getDownloadBannerText = flags => {
  return flags?.paymentAndRefundCreditConfig?.downloadAppBannerText || defaultFlags?.flags?.paymentAndRefundCreditConfig?.downloadAppBannerText;
}
const getAppRefundRevampFlowFlag = flags => {
  return (
    (flags?.appRefundRevampFlow || defaultFlags?.flags?.appRefundRevampFlow) === 'ON'
  );
}
const getEnableAjiorgramLandingPage = flags => {
  return (flags?.ajiogramLandingPage || 'OFF') === 'ON';
};

const getBannerCmsJioAdsConfig = state => {
  const { flags = {} } = state || {};
  const { bannerCmsJioAdsConfig = {} } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const bannercmsjioadsconfig = getAmplitudeVariantPayload(
      'bannercmsjioadsconfig',
      state,
      bannerCmsJioAdsConfig
    );
    return bannercmsjioadsconfig;
  }
  return flags?.bannerCmsJioAdsConfig || {};
};

const getAjioCashMopList = flags =>{
  return  (flags?.ajioCashMopList || defaultFlags?.flags?.ajioCashMopList)
}

const getBankDiscountConfig = (state) => {
  return state?.flags?.bank_discount_clawback || {};
};

const getBankDiscountEnabled = (state) => {
  const { master } = getBankDiscountConfig(state);
  return !!master;
};

const getBankDiscountCancelDesc = (state) => {
  const { cancel_desc } = getBankDiscountConfig(state);
  return cancel_desc || '';
};

const getBankDiscountReturnDesc = (state) => {
  const { return_desc } = getBankDiscountConfig(state);
  return return_desc || '';
};


const getReturnFlowOptimizationConfig = (state, msite) => {
  return msite
    ? state?.flags?.return_flow_optimization_msite ||
        defaultFlags?.flags?.return_flow_optimization_msite
    : state?.flags?.return_flow_optimization_web ||
        defaultFlags?.flags?.return_flow_optimization_web;
};

const getReturnExchangeEligibilityNudgeEnable = (state, msite = false) => {
  const { master, return_exchange_enabled } = getReturnFlowOptimizationConfig(state, msite);
  return master && return_exchange_enabled;
};

const getNewImageCollectionEnable = (state, msite = false) => {
  const { master, customer_care_enabled } = getReturnFlowOptimizationConfig(state, msite);
  return master && customer_care_enabled;
};

const getReturnRequestCalloutEnable = (state, msite = false) => {
  const { master, return_request_reason_enabled } = getReturnFlowOptimizationConfig(state, msite);
  return master && return_request_reason_enabled;
};

const getReturnRequestCalloutMessage = (state, msite = false) => {
  const { return_request_reason_desc } = getReturnFlowOptimizationConfig(state, msite);
  return return_request_reason_desc;
};

const getReturnCancellationEnable = (state, msite = false) => {
  const { master, return_cancel_enabled } = getReturnFlowOptimizationConfig(state, msite);
  return master && return_cancel_enabled;
};

const getReturnCancelPopupTitle = (state, msite = false) => {
  const { return_cancel_popup_title } = getReturnFlowOptimizationConfig(state, msite);
  return return_cancel_popup_title;
};

const getReturnCancelPopupDesc = (state, msite = false) => {
  const { return_cancel_popup_desc } = getReturnFlowOptimizationConfig(state, msite);
  return return_cancel_popup_desc;
};

const getReturnCancelPopupTimelineMsg = (state, msite = false) => {
  const { return_cancel_popup_timeline } = getReturnFlowOptimizationConfig(state, msite);
  return return_cancel_popup_timeline;
};

const getReturnCancelPopupSuccessTitle = (state, msite = false) => {
  const { return_cancel_popup_success_title } = getReturnFlowOptimizationConfig(state, msite);
  return return_cancel_popup_success_title;
};

const getUserRelevancy = flags => {
  return (
    flags?.userRelevancy || 'OFF') === 'ON';
}

const getAjioSuperCashEnable = flags => {
  return flags?.ajioSuperCashConfig?.enable === 'ON';
};

const getAjioSuperCashConfig = flags => {
  return flags?.ajioSuperCashConfig || {};
};

const getAjioSuperCashEnableForCart = (flags, storename) => {
  const { ajioSuperCashConfig = {} } = flags || {};

  const isAjio = ['ajio', 'rilfnl', ''].includes(storename?.toLowerCase());

  return (
    ajioSuperCashConfig?.enable === 'ON' &&
    ajioSuperCashConfig?.cartPageConfig?.enable === 'ON' &&
    isAjio
  );
};

const getAjioSuperCashConfigForCart = flags => {
  return flags?.ajioSuperCashConfig?.cartPageConfig || {};
};

const getAjioSuperCashConfigForPayment = (flags, storename = '') => {
  const { ajioSuperCashConfig } = flags || {};
  const { cartPageConfig = {}, paymentPageConfig, ajioCashCurrency } = ajioSuperCashConfig || {};
  const { enable = 'OFF', ...rest } = cartPageConfig || {};
  const { enable: paymentEnable = 'OFF', ...paymentRest } = paymentPageConfig || {};

  const isAjio = ['ajio', 'rilfnl', ''].includes(storename?.toLowerCase());

  return {
    enable:
      ajioSuperCashConfig?.enable === 'ON' &&
      ajioSuperCashConfig?.paymentPageConfig?.enable === 'ON' &&
      isAjio,
    ...(paymentRest || {}),
    ...(ajioCashCurrency || {}),
    ...(rest || {})
  };
};

const getAjioSuperCashBottomStripConfig = flags => {
  return flags?.ajioSuperCashConfig?.homePageConfig?.bottomStripConfig || {};
};

const getAjioSuperCashBottomStripConfigPLP = flags => {
  return flags?.ajioSuperCashConfig?.plpConfig?.bottomStripConfig || {};
};

const getAjioSuperCashBottomStripConfigPDP = flags => {
  return flags?.ajioSuperCashConfig?.pdpPageConfig?.bottomStripConfig || {};
};

const getAjioSuperCashCurrencyConfig = flags => {
  return (flags?.ajioSuperCashConfig?.ajioCashCurrency || {});
};

const getAjioSuperCashPDPEnable = flags => {
  return (
    (flags?.ajioSuperCashConfig?.enable || defaultFlags?.flags?.ajioSuperCashConfig?.enable) ===
      'ON' &&
    (flags?.ajioSuperCashConfig?.pdpPageConfig?.enable ||
      defaultFlags?.flags?.ajioSuperCashConfig?.pdpPageConfig?.enable) === 'ON'
  )
  };

const getAjioSuperCashODPEnable = flags => {
  return (
    (flags?.ajioSuperCashConfig?.enable || defaultFlags?.flags?.ajioSuperCashConfig?.enable) ===
      'ON' &&
    (flags?.ajioSuperCashConfig?.orderDetailPageConfig?.enable ||
      defaultFlags?.flags?.ajioSuperCashConfig?.orderDetailPageConfig?.enable) === 'ON'
  );
};

const getAjioSuperCashODPConfig = flags => {
  return (
    flags?.ajioSuperCashConfig?.orderDetailPageConfig ||
    defaultFlags?.flags?.ajioSuperCashConfig?.orderDetailPageConfig
  );
};

const getAjioSuperCashTYPEnable = flags => {
  return (
    (flags?.ajioSuperCashConfig?.enable || defaultFlags?.flags?.ajioSuperCashConfig?.enable) ===
      'ON' &&
    (flags?.ajioSuperCashConfig?.thankYouPageConfig?.enable ||
      defaultFlags?.flags?.ajioSuperCashConfig?.thankYouPageConfig?.enable) === 'ON'
  );
};

const getAjioSuperCashEnableMaster = (state, configPath) => {
  const ajioSuperCashConfig = state?.flags?.ajioSuperCashConfig || {};
  const currentStore = getCurrentStore(state) || '';
  const isAjio = ['ajio', 'rilfnl', ''].includes(currentStore?.toLowerCase());

  if (!isAjio) return false;

  if (!ajioSuperCashConfig) return false;

  const isPageConfigEnabled = ajioSuperCashConfig[configPath]?.enable === 'ON';
  const isMasterEnabled = ajioSuperCashConfig?.enable === 'ON';

  return isPageConfigEnabled && isMasterEnabled;
};

const getAjioSuperCashWalletEnable = (state) => {
  return getAjioSuperCashEnableMaster(state, 'walletPageConfig');
};

const getAjioSuperCashPdpEnable = (state) => {
  return getAjioSuperCashEnableMaster(state, 'pdpPageConfig');
};

const getAjioSuperCashHomeEnable = (state) => {
  return getAjioSuperCashEnableMaster(state, 'homePageConfig');
};

const getAjioSuperCashPlpEnable = (state) => {
  return getAjioSuperCashEnableMaster(state, 'plpConfig');
};

const getAjioSuperCashExpiryTemplate = flags => {
  return flags?.ajioSuperCashConfig?.walletPageConfig?.expiryTemplate;
};
const getAjioCashCurrencyName = flags => {
  return flags?.ajioSuperCashConfig?.ajioCashCurrency?.currencyName;
};
const getSegmentTrackingEnabledFlag = flags => {
  return (    
    flags?.segment_tracking_enabled || 'ON');
    
}

const getPlpOnLandingConfig = state => {
  if (state?.flags.amplitude_experiment_flag === 'ON') {
    return (
      getAmplitudeVariantPayload('plpOnLanding', state, state?.flags?.plpOnLanding) ||
      state?.flags?.plpOnLanding ||
      defaultPlpOnLandingConfig
    );
  }
  return {};
};

const getPlpOnLandingEnabled = state => {
  const plpOnLandingConfig = getPlpOnLandingConfig(state);
  return plpOnLandingConfig?.enablePlpOnLanding === 'ON';
};

const getPlpOnLandingTitleEnabled = state => {
  const plpOnLandingConfig = getPlpOnLandingConfig(state);
  return plpOnLandingConfig?.enableTitle === 'ON';
};

const getFilterTabsOnLandingEnabled = state => {
  const plpOnLandingConfig = getPlpOnLandingConfig(state);
  return plpOnLandingConfig?.showFilter || false;
};

const getWidgetCoverageSearchFlag = (state, currentStore) => {
  const isAjio = ['ajio', 'rilfnl', ''].includes(currentStore?.toLowerCase());

  if (state?.flags?.amplitude_experiment_flag === 'ON') {
    let widgetCoverageFlags = getAmplitudeVariantPayload(
          'widgetCoverageFlags',
          state,
          defaultFlags?.flags?.widgetCoverageFlags
        );

      return (
        widgetCoverageFlags?.enableWidgetCoverageMaster === 'ON' &&
        widgetCoverageFlags?.enableWidgetCoverageSearchPage=== 'ON' &&
        isAjio
      );
    }
    return (
        defaultFlags?.flags?.widgetCoverageFlags?.enableWidgetCoverageMaster === 'ON' &&
        defaultFlags?.flags?.widgetCoverageFlags?.enableWidgetCoverageSearchPage=== 'ON' &&
        isAjio
      );
}

const getWidgetCoveragePDPFlag = (state, currentStore) => {
  const isAjio = ['ajio', 'rilfnl', ''].includes(currentStore?.toLowerCase());

  if (state?.flags?.amplitude_experiment_flag === 'ON') {
    let widgetCoverageFlags = getAmplitudeVariantPayload(
          'widgetCoverageFlags',
          state,
          defaultFlags?.flags?.widgetCoverageFlags
        );

      return (
        widgetCoverageFlags?.enableWidgetCoverageMaster === 'ON' &&
        widgetCoverageFlags?.enableWidgetCoveragePDP=== 'ON' &&
        isAjio
      );
    }
    return (
        defaultFlags?.flags?.widgetCoverageFlags?.enableWidgetCoverageMaster === 'ON' &&
        defaultFlags?.flags?.widgetCoverageFlags?.enableWidgetCoveragePDP=== 'ON' &&
        isAjio
      );
}

const getPlaAdsOnPDPFlag = (state, currentStore) => {
  // const isAjio = ['ajio', 'rilfnl', ''].includes(currentStore?.toLowerCase());
 
  const { flags } = state;
  const { plaAdsFlags = {} } = flags || {};
  const plaFlags = getAmplitudeVariantPayload('pla-jioads-on-pdp',state, plaAdsFlags);
  // console.log("🚀 ~ getPlaAdsFlags ~ plaFlag master", state, plaFlags)
  return (
    plaFlags?.plaAdsFlags?.enableMaster && plaFlags?.plaAdsFlags?.pdp_page?.isEnabled
  );
};
const getPlaAdsFlags = (state) => {
  const { flags } = state;
  const { plaAdsFlags = {} } = flags || {};
  const plaFlags = getAmplitudeVariantPayload('pla-jioads-on-pdp',state, plaAdsFlags);
  // console.log("🚀 ~ getPlaAdsFlags ~ plaFlags:", plaFlags)
  return plaFlags?.plaAdsFlags?.pdp_page;
};
const getPlaAdsClientID = (state) => {
  const { flags } = state;
  const { plaAdsFlags = {} } = flags || {};
  const plaFlags = getAmplitudeVariantPayload('pla-jioads-on-pdp',state, plaAdsFlags);
  return plaFlags?.plaAdsFlags?.clientId;
};



const getWidgetCoverageThankYouFlag = (state, currentStore) => {
  const isAjio = ['ajio', 'rilfnl', ''].includes(currentStore?.toLowerCase());

  if (state?.flags?.amplitude_experiment_flag === 'ON') {
    let widgetCoverageFlags = getAmplitudeVariantPayload(
          'widgetCoverageFlags',
          state,
          defaultFlags?.flags?.widgetCoverageFlags
        );

      return (
        widgetCoverageFlags?.enableWidgetCoverageMaster === 'ON' &&
        widgetCoverageFlags?.enableWidgetCoverageThankYou=== 'ON' &&
        isAjio
      );
    }
    return (
        defaultFlags?.flags?.widgetCoverageFlags?.enableWidgetCoverageMaster === 'ON' &&
        defaultFlags?.flags?.widgetCoverageFlags?.enableWidgetCoverageThankYou=== 'ON' &&
        isAjio
      );
}

const getPLPProductLoadMargin = flags => {
  return flags?.plpProductLoadMargin || defaultFlags?.flags?.plpProductLoadMargin;
};

const getCopilotEnabledMsiteFlag = (flags={}) => {
  return flags?.copilotConfig?.copilotMsiteEnabled || 'OFF';   
};

const getCopilotEnabledDesktopFlag = (flags={}) => {
  return flags?.copilotConfig?.copilotDesktopEnabled || 'OFF';   
};

const getcopilotWhitelistedPagesDesktop = (flags={}) => {
  return flags?.copilotConfig?.copilotWhitelistedPagesDesktop || defaultFlags?.flags?.copilotConfig?.copilotWhitelistedPagesDesktop;
};

const getcopilotWhitelistedPagesMsite = (flags={}) => {
  return flags?.copilotConfig?.copilotWhitelistedPagesMsite || defaultFlags?.flags?.copilotConfig?.copilotWhitelistedPagesMsite;
};

const getcopilotWhitelistedStoresDesktop = (flags={}) => {
  return flags?.copilotConfig?.copilotWhitelistedStoresDesktop || defaultFlags?.flags?.copilotConfig?.copilotWhitelistedStoresDesktop;
};

const getHideIngressMsite = (flags={}) => {
  return flags?.copilotConfig?.hideIngressMsite || defaultFlags?.flags?.copilotConfig?.hideIngressMsite;
};

const getHideIngressDesktop = (flags={}) => {
  return flags?.copilotConfig?.hideIngressDesktop || defaultFlags?.flags?.copilotConfig?.hideIngressDesktop;
};

const getcopilotWhitelistedStoresMsite = (flags={}) => {
  return flags?.copilotConfig?.copilotWhitelistedStoresMsite || defaultFlags?.flags?.copilotConfig?.copilotWhitelistedStoresMsite;
};

const getSizeRecommendationConfig = (flags = {}) => {
  //TODO: temp changes - to be reverted
  // return {
  //   enable: 'ON',
  //   environment: 'sit',
  //   accessToken: 'e5a9f4196ff1d9af771dd1f298b1fbcd4fa889d856bcc2bc489e7f39e4b0d5d8',
  //   storeType: ['ajio', 'luxe', 'ajiogram'],
  //   iconUrls: {
  //     ajio: 'https://assets-uat.ajio.ril.com/cms/AJIO/MOBILE/fyndsize.png',
  //     ajiogram: 'https://assets-uat.ajio.ril.com/cms/AJIO/MOBILE/fyndsize.png',
  //     luxe: 'https://assets-uat.ajio.ril.com/cms/AJIO/MOBILE/fyndsize_luxe.png'
  //   },
  //   texts: {
  //     findYourSizeText: 'Find your recommended size',
  //     noSizeRecommendedText: 'No size recommendation available',
  //     sizeText: 'Size ${size}',
  //     recommendedSizeText: 'is recommended basis your inputs.',
  //     editCTA: 'Edit info',
  //     tryAgaingText: 'Hmm, something’s off.',
  //     tryAgainCTA: 'Try again',
  //     toolOfflineText: 'Oops, the tool’s offline. Please try later.'
  //   },
  //   sizeConfig: [
  //     {
  //       segment: 'Men',
  //       vertical: 'Western Wear',
  //       brick: 'Shirts'
  //     },
  //     {
  //       segment: 'Men',
  //       vertical: 'Western Wear',
  //       brick: 'Jeans'
  //     },
  //     {
  //       segment: 'Men',
  //       vertical: 'Western Wear',
  //       brick: 'Jackets & Coats'
  //     },
  //     {
  //       segment: 'Men',
  //       vertical: 'Western Wear',
  //       brick: 'Trousers & Pants'
  //     },
  //     {
  //       segment: 'Women',
  //       vertical: 'Western Wear',
  //       brick: 'Dresses'
  //     },
  //     {
  //       segment: 'Men',
  //       vertical: 'Western Wear',
  //       brick: 'Sweatshirts & Hoodie'
  //     },
  //     {
  //       segment: 'Men',
  //       vertical: 'Western Wear',
  //       brick: 'Sweatshirt & Hoodies'
  //     },
  //     {
  //       segment: 'Women',
  //       vertical: 'Western Wear',
  //       brick: 'Jeans & Jeggings'
  //     },
  //     {
  //       segment: 'Men',
  //       vertical: 'Western Wear',
  //       brick: 'Track Pants'
  //     },
  //     {
  //       segment: 'Women',
  //       vertical: 'Western Wear',
  //       brick: 'Tops'
  //     },
  //     {
  //       segment: 'Women',
  //       vertical: 'Western Wear',
  //       brick: 'Sweaters & Cardigans'
  //     },
  //     {
  //       segment: 'Women',
  //       vertical: 'Western Wear',
  //       brick: 'Sweatshirts &Hoodies'
  //     },
  //     {
  //       segment: 'Men',
  //       vertical: 'Western Wear',
  //       brick: 'Blazers & Waistcoats'
  //     },
  //     {
  //       segment: 'Women',
  //       vertical: 'Western Wear',
  //       brick: 'Dresses & Gowns'
  //     },
  //     {
  //       segment: 'Women',
  //       vertical: 'Western Wear',
  //       brick: 'Tshirts'
  //     },
  //     {
  //       segment: 'Men',
  //       vertical: 'Western Wear',
  //       brick: 'Shorts & 3/4ths'
  //     },
  //     {
  //       segment: 'Women',
  //       vertical: 'Western Wear',
  //       brick: 'Leggings'
  //     },
  //     {
  //       segment: 'Women',
  //       vertical: 'Western Wear',
  //       brick: 'Shirts'
  //     },
  //     {
  //       segment: 'Women',
  //       vertical: 'Western Wear',
  //       brick: 'Bras'
  //     },
  //     {
  //       brick: 'Tshirts',
  //       segment: 'Men',
  //       vertical: ''
  //     },

  //     {
  //       brick: 'Jeans',
  //       segment: '',
  //       vertical: ''
  //     }
  //   ]
  // };
  return flags?.sizeRecommendationConfig || {};
};

const getUrgencyDriverV2PLPEnable = state => {
  const { flags = {} } = state || {};

  if (flags.amplitude_experiment_flag === 'ON') {
    const amplitudeFlags =
      getAmplitudeVariantPayload(
        'urgencydriverv2msiteconfig',
        state,
        flags?.urgencyDriverV2MsiteConfig
      ) || {};
    const { enableMaster = 'OFF', plpPage = {} } = amplitudeFlags;
    const { enable: enablePLPPage = 'OFF' } = plpPage;

    return enableMaster === 'ON' && enablePLPPage === 'ON';
  }

  return false;
};

const getUrgencyDriverV2PDPEnable = state => {
  const { flags = {} } = state || {};

  if (flags.amplitude_experiment_flag === 'ON') {
    const amplitudeFlags =
      getAmplitudeVariantPayload(
        'urgencydriverv2msiteconfig',
        state,
        flags?.urgencyDriverV2MsiteConfig
      ) || {};
    const { enableMaster = 'OFF', pdpPage = {} } = amplitudeFlags;
    const { enable: enablePDPPage = 'OFF' } = pdpPage;

    return enableMaster === 'ON' && enablePDPPage === 'ON';
  }

  return false;
};

const getUrgencyDriverV2CartEnable = state => {
  const { flags = {} } = state || {};

  if (flags.amplitude_experiment_flag === 'ON') {
    const amplitudeFlags =
      getAmplitudeVariantPayload(
        'urgencydriverv2msiteconfig',
        state,
        flags?.urgencyDriverV2MsiteConfig
      ) || {};
    const { enableMaster = 'OFF', cartPage = {} } = amplitudeFlags;
    const { enable: enableCartPage = 'OFF' } = cartPage;

    return enableMaster === 'ON' && enableCartPage === 'ON';
  }

  return false;
};

const getUrgencyDriverV2Config = state => {
  const { flags = {} } = state || {};

  if (flags.amplitude_experiment_flag === 'ON') {
    const amplitudeFlags =
      getAmplitudeVariantPayload(
        'urgencydriverv2msiteconfig',
        state,
        flags?.urgencyDriverV2MsiteConfig
      ) || {};
    return amplitudeFlags || {};
  }

  return {};
};

const getGSTFeatureConfig = state => {
  const { flags = {} } = state;
  return (() => {
    if (flags.amplitude_experiment_flag === 'ON') {
      return getAmplitudeVariantPayload('gstfeatureconfig', state, {}) || '';
    }
    return flags?.gstFeatureConfig || defaultFlags?.flags?.gstFeatureConfig || {};
  })();
};

const getGSTFlag = (state, pageName = 'cart') => {
  const { flags = {}, metaData: { name } = {} } = state || {};
  let currentStore = getCurrentStore(state)?.toLowerCase();
  if (currentStore === 'rilfnl' || currentStore === '') {
    currentStore = 'ajio';
  }
  const storeName = name && String(name).toLowerCase() !== 'ajio' ? name : 'rilfnl';
  const enableUniversalCohortFlow = getAmplitudeVariantPayload('enableUniversalCohortFlow', state, false);
  const cohortIds = getUserCohortIdsForClient(
    storeName,
    pageName,
    enableUniversalCohortFlow
  )?.split(/[|,]/);
  const gstFetureConfig = getGSTFeatureConfig(state);
  const gstCohortIds = gstFetureConfig?.platforms?.[currentStore]?.cohorts_exclusion;

  const gstCohortEnable =
    Array.isArray(gstCohortIds) && cohortIds?.length
      ? gstCohortIds.some((id: string) => cohortIds.includes(id))
      : false;

  const enableGSTFlag = gstFetureConfig?.masterFlag || false;

  if (flags.amplitude_experiment_flag === 'ON') {
    return (
      (!gstCohortEnable && enableGSTFlag) || false
    );
  }

  return !gstCohortEnable && enableGSTFlag;
};

const getGSTCommonConfig = state => getGSTFeatureConfig(state)?.gstConfig?.common || {};

const getGSTCartConfig = state => getGSTFeatureConfig(state)?.gstConfig?.cart || {};

const getGSTODPConfig = state => getGSTFeatureConfig(state)?.gstConfig?.odp || {};

const getGstEnhancementConfig = state => {
  const { flags = {} } = state;
  const { gstEnhancementConfig = {} } = flags || {};
  return (() => {
    if (flags.amplitude_experiment_flag === 'ON') {
      return getAmplitudeVariantPayload('gstenhancementconfig', state, gstEnhancementConfig) || {};
    }
    return gstEnhancementConfig || defaultFlags?.flags?.gstEnhancementConfig || {};
  })();
};

const getOrderConfirmGstEnhancementTextConfig = state => {
  return {
    ...(getGstEnhancementConfig(state)?.textConfig?.orderConfirm || {})
  };
};

const getShippingGstEnhancementTextConfig = state => {
  return {
    ...(getGstEnhancementConfig(state)?.textConfig?.shippingTextConfig || {})
  };
};

const getGstEnhancementFlag = (state, pageName = 'cart') => {
  const { flags = {}, metaData: { name } = {} } = state || {};
  let currentStore = getCurrentStore(state)?.toLowerCase();
  if (currentStore === 'rilfnl' || currentStore === '') {
    currentStore = 'ajio';
  }
  const storeName = name && String(name).toLowerCase() !== 'ajio' ? name : 'rilfnl';
  const enableUniversalCohortFlow = getAmplitudeVariantPayload('enableUniversalCohortFlow', state, false);
  const cohortIds = getUserCohortIdsForClient(
    storeName,
    pageName,
    enableUniversalCohortFlow
  )?.split(/[|,]/);
  const gstEnhancementConfig = getGstEnhancementConfig(state);
  const gstCohortIds = gstEnhancementConfig?.platforms?.[currentStore]?.cohorts_exclusion;

  const gstCohortEnable =
    Array.isArray(gstCohortIds) && cohortIds?.length
      ? gstCohortIds.some((id: string) => cohortIds.includes(id))
      : false;

  const enableGSTEnhancementFlag = gstEnhancementConfig?.master || false;

  if (flags.amplitude_experiment_flag === 'ON') {
    return (!gstCohortEnable && enableGSTEnhancementFlag) || false;
  }

  return !gstCohortEnable && enableGSTEnhancementFlag;
};

const getEnableFilterImprovement = state => {
  const { flags = {} } = state;

  if (flags.amplitude_experiment_flag === 'ON') {
    const filterImprovementFlags = getAmplitudeVariantPayload('filterImprovement', state, {}) || {};

    return !!filterImprovementFlags?.isEnabled;
  }

  return false;
};

const getEnableFilterImprovementPhase2 = state => {
  const { flags = {} } = state;

  if (flags.amplitude_experiment_flag === 'ON') {
    const filterImprovementFlags = getAmplitudeVariantPayload('filterImprovement', state, {}) || {};
    const { isEnabled = false, isPhase2Enabled = false } = filterImprovementFlags || {};

    return isEnabled && isPhase2Enabled;
  }

  return false;
};

const getFilterImprovementConfig = state => {
  const { flags = {} } = state;

  if (flags.amplitude_experiment_flag === 'ON') {
    return getAmplitudeVariantPayload('filterImprovement', state, {}) || {};
  }

  return {};
};

const getEmiDiscountConfig = state => {
  const { flags = {} } = state;
  if (flags.amplitude_experiment_flag === 'ON') {
    return getAmplitudeVariantPayload('emidiscountconfig', state, flags?.emidiscountconfig) || {};
  }
  return flags?.emidiscountconfig;
};

const getEmiDiscountEnable = state => {
  const emiConfigData = getEmiDiscountConfig(state);
  return emiConfigData?.masterFlag;
};

const getEmiDiscountEnableForPDP = state => {
  let currentStore = getCurrentStore(state)?.toLowerCase();
  if (currentStore === 'rilfnl' || currentStore === '') {
    currentStore = 'ajio';
  }

  return (
    getEmiDiscountEnable(state) && getEmiDiscountConfig(state)?.platforms[currentStore]?.emiOnPDP
  );
};

const getEmiDiscountEnableForSPC = state => {
  let currentStore = getCurrentStore(state)?.toLowerCase();
  if (currentStore === 'rilfnl' || currentStore === '') {
    currentStore = 'ajio';
  }
  return (
    getEmiDiscountEnable(state) &&
    getEmiDiscountConfig(state)?.platforms[currentStore]?.emiOnPayment
  );
};
const getEmiTemplate = (state) => {
  const emiConfigData = getEmiDiscountConfig(state);
  return  emiConfigData?.pdp?.emiTemplates;
};

const getEmiThresholdAmount = (state) => {
  let currentStore = getCurrentStore(state)?.toLowerCase();
  if(currentStore === 'rilfnl' || currentStore === '') {
    currentStore = 'ajio'
  };

  return getEmiDiscountConfig(state)?.platforms?.[currentStore]?.thresholdAmount;
};

const getOfferCardOrder = (state) => {
  let currentStore = getCurrentStore(state)?.toLowerCase();
  if(currentStore === 'rilfnl' || currentStore === '') {
    currentStore = 'ajio'
  };

  return getEmiDiscountConfig(state)?.platforms?.[currentStore]?.offerOrder;
};

const getViewMoreEmiConfig = (state) => {
  const config = getEmiDiscountConfig(state) || {};

  return {
    ...config?.pdp?.emiPlanMessages,
    ...config?.common
  };
};

const getEmiDiscountSPCConfig = state => {
  const config = getEmiDiscountConfig(state) || {};

  return {
    ...config?.spc,
    ...config?.common
  };
};
const getRatingParityMsiteFlag = state => {
  const { flags = {} } = state;

  if (flags.amplitude_experiment_flag === 'ON') {
    const msiteParityFeature = getAmplitudeVariantPayload('msiteparityfeature', state, {}) || {};
    const { ratingParityMsite = 'OFF' } = msiteParityFeature || {};
    return ratingParityMsite == 'ON' ? true : false;
  }

  return false;
};

const getOosSimilarProductFlag = state => {
  const { flags = {} } = state;

  if (flags.amplitude_experiment_flag === 'ON') {
    const msiteParityFeature = getAmplitudeVariantPayload('msiteparityfeature', state, {}) || {};
    const { oosSimilarProduct = 'OFF' } = msiteParityFeature || {};
    return oosSimilarProduct == 'ON' ? true : false;
  }

  return false;
 };

const getIsLimitedScrollLandingPageEnabled = state => {
  const { flags = {} } = state;

  if (flags.amplitude_experiment_flag === 'ON') {
    const limitedScrollLandingPageConfig =
      getAmplitudeVariantPayload('limited_scroll_landing_page', state, {}) || {};
    const { master: isLimitedScrollLandingPageEnabled = false } =
      limitedScrollLandingPageConfig || {};
    return isLimitedScrollLandingPageEnabled;
  }

  return false;
};

const getLimitedScrollLandingPageConfig = state => {
  const { flags = {} } = state;

  if (flags.amplitude_experiment_flag === 'ON') {
    const limitedScrollLandingPageConfig =
      getAmplitudeVariantPayload('limited_scroll_landing_page', state, {}) || {};
    return limitedScrollLandingPageConfig || {};
  }

  return {};
 };
const getAffiliateFeatureConfig = state => {
  const { flags = {} } = state || {};
  if (flags.amplitude_experiment_flag === 'ON') {
    return (
      getAmplitudeVariantPayload('affiliatefeatureconfig', state, flags?.affiliateFeatureConfig) ||
      {}
    );
  }
  return flags?.affiliateFeatureConfig;
};

const getAffiliateAttributionWindow = state => {
  const { flags = {} } = state || {};
  if (flags.amplitude_experiment_flag === 'ON') {
    return (
      getAmplitudeVariantPayload(
        'affiliateattributionwindow',
        state,
        flags?.affiliateAttributionWindow
      ) || 24
    );
  }
  return flags?.affiliateAttributionWindow || 24;
};

const getCompanionMsiteConfig = state => {
  const { flags = {} } = state || {};
  if (flags.amplitude_experiment_flag === 'ON') {
    const isCompanionsFlowEnabled =
      getAmplitudeVariantPayload('companionmsiteconfig', state, flags?.companionMsiteConfig) || {};
    return isCompanionsFlowEnabled?.isEnabled
  }
  return flags?.companionMsiteConfig?.isEnabled;
};
const getjioVaultConfig = state => {
  const { flags = {} } = state || {};
  if (flags.amplitude_experiment_flag === 'ON') {
    const isJioVaultEnabled =
      getAmplitudeVariantPayload('jioVault', state, flags?.jioVault) || {};
    return isJioVaultEnabled
  }
  return flags?.jioVault;
};

const getJioAdsLandingBannersFlag = state => {
  const { flags = {} } = state || {};
  const { jioAdsLandingBannersFlag = 'OFF' } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const jioadslandingbannersflag = getAmplitudeVariantPayload(
      'jioadslandingbannersflag',
      state,
      jioAdsLandingBannersFlag
    );
    return jioadslandingbannersflag === 'ON';
  }
  return (flags?.jioAdsLandingBannersFlag || 'OFF') === 'ON';
};

const getEnableAdsOnSlp = state => {
  const { flags = {} } = state || {};
  const { enableAdsOnSlp = 'OFF' } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const enableadsonslp = getAmplitudeVariantPayload('enableadsonslp', state, enableAdsOnSlp);
    return enableadsonslp === 'ON';
  }
  return (flags?.enableAdsOnSlp || 'OFF') === 'ON';
};

const getEnableAdsOnPlp = state => {
  const { flags = {} } = state || {};
  const { enableAdsOnPlp = 'OFF' } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const enableadsonplp = getAmplitudeVariantPayload('enableadsonplp', state, enableAdsOnPlp);
    return enableadsonplp === 'ON';
  }
  return (flags?.enableAdsOnPlp || 'OFF') === 'ON';
};

const getBannerOsmosAdsConfig = state => {
  const { flags = {} } = state || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    const osmosbannerads = getAmplitudeVariantPayload('osmos_banner_ads', state, {});
    return osmosbannerads || {};
  }
  return {};
};

const defaultCrossSellV2CartPageConfig = getValue(defaultFlags, 'flags.crossSellV2.cart_page');

const getCrossSellV2CartEnable = state => {
  const { flags = {} } = state || {};
  const { crossSellV2 = {} } = flags;
  if (flags?.amplitude_experiment_flag === 'ON') {
    const crosssellv2Config = getAmplitudeVariantPayload('crosssellv2', state, crossSellV2);
    return !!(crosssellv2Config?.master && crosssellv2Config?.cart_page?.cartEnable);
  }
  return !!(crossSellV2?.master && crossSellV2?.cart_page?.cartEnable);
};

const getCrossSellV2CartPageConfig = state => {
  const { flags = {} } = state || {};
  const { crossSellV2 = {} } = flags;
  if (flags?.amplitude_experiment_flag === 'ON') {
    const crosssellv2Config = getAmplitudeVariantPayload('crosssellv2', state, crossSellV2);
    return crosssellv2Config?.cart_page || defaultCrossSellV2CartPageConfig;
  }
  return crossSellV2?.cart_page || defaultCrossSellV2CartPageConfig;
};

const getTrendBasedDiscoveryConfig = state => {
  const { flags = {} } = state;
  const { trendBasedDiscoveryConfig = {} } = flags;

  return (() => {
    if (flags.amplitude_experiment_flag === 'ON') {
      return (
        getAmplitudeVariantPayload('trendbaseddiscoveryconfig', state, trendBasedDiscoveryConfig) ||
        {}
      );
    }
    return trendBasedDiscoveryConfig || {};
  })();
};

const getTrendBasedDiscoveryFlag = (state, pageName = 'pdp') => {
  const trendBasedDiscoveryConfig = getTrendBasedDiscoveryConfig(state);
  const isTrendBasedDiscoveryEnable = trendBasedDiscoveryConfig?.master || false;
  if (isTrendBasedDiscoveryEnable) {
    const pageConfig = trendBasedDiscoveryConfig?.[pageName] || {};
    const maxTrendsCount = trendBasedDiscoveryConfig?.maxTrendsCount;
    return maxTrendsCount != null ? { ...pageConfig, maxTrendsCount } : pageConfig;
  }
  return {};
}

const getVertexEnabledAjiogram = state => {
  const { flags = {} } = state || {};
  if (flags.amplitude_experiment_flag === 'ON') {
    return (
      getAmplitudeVariantPayload('vertexEnabledAjiogram', state, flags?.vertexEnabledAjiogram) || {}
    );
  }
  return flags?.vertexEnabledAjiogram;
};

const getVertexEnabledLuxe = state => {
  const { flags = {} } = state || {};
  if (flags.amplitude_experiment_flag === 'ON') {
    return getAmplitudeVariantPayload('vertexEnabledLuxe', state, flags?.vertexEnabledLuxe) || {};
  }
  return flags?.vertexEnabledLuxe;
}
  
const getVertexEnabledForSis = state => {
  const { flags = {} } = state || {};
  if (flags.amplitude_experiment_flag === 'ON') {
    return !!getAmplitudeVariantPayload('vertexEnabledForSis', state, flags?.vertexEnabledForSis);
  }
  return !!flags?.vertexEnabledForSis;
};

const getVisualFiltersConfig = state => {
  const { flags = {} } = state || {};

  if (flags.amplitude_experiment_flag === 'ON') {
    const visualfiltersconfig = getAmplitudeVariantPayload('visualfiltersconfig', state, {}) || {};
    return visualfiltersconfig || {};
  }

  return {};
};

const getOLPFilterImprovementConfig = state => {
  const { flags = {} } = state || {};

  if (flags.amplitude_experiment_flag === 'ON') {
    return getAmplitudeVariantPayload('olp_filter_improvement', state, {}) || {};
  }

  return {};
};

const getRedemptionOptionsOnCartFlag = state => {
  const { flags = {} } = state || {};
  if (flags.amplitude_experiment_flag === 'ON') {
    const redemptionOptionsOnCartFlag = getAmplitudeVariantPayload('enableRedemptionOptionsOnCart', state, false);
    return redemptionOptionsOnCartFlag === 'ON';
  }

  return (flags?.enableRedemptionOptionsOnCart || 'OFF') === 'ON';
};

const getCrossSellV2Enabled = (state) => {
  const { flags = {}} = state || {};
  const { crossSellV2 = {} } = flags || {};
  if (flags?.amplitude_experiment_flag === 'ON') {
    console.log("FEATURE FLAGS SELECTOR")
    const crosssellv2Config = getAmplitudeVariantPayload('crosssellv2', state, crossSellV2);
    console.log("FEATURE FLAGS SELECTOR", crosssellv2Config)
    
    return crosssellv2Config || {};
  }
  console.log("FIREBASE FLAG", crossSellV2)
  return crossSellV2;
}
const getCancelRefundImprovementConfig = state => {
  const { flags = {} } = state || {};

  if (flags.amplitude_experiment_flag === 'ON') {
    return getAmplitudeVariantPayload('cancel_refund_improvement_config', state, {}) || {};
  }

  return {};
};

const getIsCancelRefundImprovementEnabled = state => {
  const cancelRefundImprovementConfig = getCancelRefundImprovementConfig(state);
  return cancelRefundImprovementConfig?.master || false;
};

const getEnableNonReturnableProductRevamp = state => {
  const { flags = {} } = state || {};

  if (flags.amplitude_experiment_flag === 'ON') {
    const payload = getAmplitudeVariantPayload(
      'enablenonreturnableproductrevamp',
      state,
      flags?.enableNonReturnableProductRevamp
    );
    return payload?.isEnabled || false;
  }

  return flags?.enableNonReturnableProductRevamp?.isEnabled || false;
};

const getEnableNonReturnableProductRevampConfig = state => {
  const { flags = {} } = state || {};

  if (flags.amplitude_experiment_flag === 'ON') {
    const { isEnabled, ...config } =
      getAmplitudeVariantPayload(
        'enablenonreturnableproductrevamp',
        state,
        flags?.enableNonReturnableProductRevamp
      ) || {};
    return config;
  }

  const { isEnabled, ...config } = flags?.enableNonReturnableProductRevamp || {};
  return config;
};

const getKaptureChatConfig = state => {
  const { flags = {} } = state || {};

  if (flags.amplitude_experiment_flag === 'ON') {
    return getAmplitudeVariantPayload('kapture_chatbot_config', state, {}) || {};
  }

  return {};
};

const getOrderDetailsJcpMigrationConfig = state => {
  const { flags = {} } = state || {};

  if (flags.amplitude_experiment_flag === 'ON') {
    return getAmplitudeVariantPayload('order_details_jcp_migration', state, {}) || {};
  }

  return {};
};

const getCreatorProgramConfig = state => {
  const { flags = {} } = state || {};
  if (flags.amplitude_experiment_flag === 'ON') {
    return (
      getAmplitudeVariantPayload('creatorprogramconfig', state, flags?.creatorprogramconfig) ||
      {}
    );
  }
  return flags?.creatorprogramconfig || {};
};

const getCountDisplayConfig = state => {
  const { flags = {} } = state || {};
  return flags.amplitude_experiment_flag === 'ON'
    ? getAmplitudeVariantPayload('count_display_config', state, flags?.count_display_config) || {}
    : flags?.count_display_config || {};
};

// Search Page Messaging config (spell correction / ZSR / low-result banner copy + store gate).
// Its own top-level feature flag, independent of the count-display config. Copy templates live
// in featureFlags.js (search_messaging_config.messages) and use inline markup, not this file.
const getSearchMessagingConfig = state => {
  const { flags = {} } = state || {};
  if (flags.amplitude_experiment_flag === 'ON') {
    return (
      getAmplitudeVariantPayload(
        'search_messaging_config',
        state,
        flags?.search_messaging_config
      ) || {}
    );
  }
  return flags?.search_messaging_config || {};
};

export {
  getFeatureFlags,
  getNewSizeRevampFlag,
  getDisableGSTContentFlag,
  getDefaultGSTMessage,
  getMarkAsDeliveredFlag,
  getRuntimeBannersMsiteFlag,
  getRuntimeBannersMsiteSisFlag,
  getRuntimeBannersMsiteWithSisFlag,
  getjewelReturnMessageFlag,
  getjewelReturnModalMessage,
  getReturnCapture,
  getEnablePwaInstall,
  getLuxeFlags,
  getNewUserBannerFlag,
  getAppInstallFlag,
  getAppConfig,
  getPriceRevealV2MobileFeatureFlag,
  getPriceRevealMobileData,
  getPriceRevealLUXEFlag,
  getUspsPayment,
  getPaymentMsg,
  getCodMessage,
  getShowStorePickUpTab,
  getPickFromStoreFlag,
  getEGiftCard,
  getShoppingAssistantFlag,
  getShoppingAssistantData,
  getNewBrandDirectoryMsiteFlag,
  getNewBrandDirectoryDesktopFlag,
  getAssuredGifts,
  getAssuredFlags,
  getAssuredGiftsFlag,
  getBankOffersPDPAggregator,
  getBankOffersHybris,
  getCodConvenienceData,
  getEarlyAccessVariant,
  getAjioWallet,
  getUaas,
  getSearchPageVersion,
  getSearchPageVersionLuxe,
  getBrandCompare,
  getBrickCodeList,
  getsizeChartSizeClick,
  getDeleteForNotifyMeFlag,
  getNotifyMessageConfig,
  getSingleRecommend,
  getBrandSizeToggleText,
  getBrandSizeToggleBtn,
  getAllOffers,
  getFreeShippingAmountLimit,
  getFreeDeliveryPdpTagFlag,
  getProductImageInColourPalletFlag,
  getVersionData,
  getProductCountWishlistWidgetSearchScreen,
  getDataNotFoundText,
  getShowSimilarProductsForPlp,
  getNewOfferBenefitsConfig,
  getInternalWalletFlag,
  getShowHomeRating,
  getShowPDPRateSection,
  getShowRateProdBtn,
  getEnableRating,
  getViewTypeFlag,
  getHowToMeasureVisibleInSizeGuideFlag,
  getHowToMeasureNewImagesFlag,
  getHowToMeasureNewImagesProducts,
  getBannerLoginPageFlag,
  getBannerSignupPageFlag,
  getBannerLoginPage,
  getBannerSignupPage,
  getEnableReturnBankInfoEncryption,
  getEnableSaveImpsBankEncryption,
  getPublicKey,
  getVersionForEncryption,
  getKnowTheProductBrickCodeList,
  getShowRateCalculate,
  getRateCalculateText,
  getLoginToApplyCouponCTA,
  getLoggedInApplyCouponToast,
  getBagChangeInterval,
  getLuxeBrandPage,
  getUnlockedText,
  getFloatingWidgetFlags,
  getjioAdStartPositionMSite,
  getjioAdsEndPositionMsite,
  getjioAdsFrequencyMsite,
  getjioAdsStartRowDesktop,
  getjioAdsEndRowDesktop,
  getjioAdsRowCount,
  getWishlistUnratedProductsWidgetPosition,
  getPLPUnratedProductsWigetPosition,
  getBusinessCancellationMessage,
  getEnableRatingLabelCart,
  getEnableRatingLabelPLP,
  getEnableRatingLabelWishlist,
  getTncListData,
  getAjioMassEmailIdOptionalMsite,
  getStreetFlags,
  getAjioStreetThemeFlags,
  getStoresForHidingMRP,
  getShowSimilarProducts,
  getSimilarProductsMaxLimit,
  getBagTotalNotAvailableForOrders,
  getExchangeRTOScreensUpdate,
  getEnableReviewFlag,
  getPrivacyPolicy,
  getShowAdTagBannerListingAds,
  getSearchFlags,
  getFileSize,
  getShowReviewProdBtn,
  getUserConsent,
  getMasterFlagFleek,
  getMasterGA3Flag,
  getMasterGA4Flag,
  getReleaseGA4Flag,
  getShowClosedtoOpenBox,
  getTradeDiscountValueExpFlag,
  getTradeDiscountPercentageMax,
  getTradeDiscountValueMin,
  getShowConvertedGifs,
  getEnableProductCodeTag,
  getEnableDeliveryFeeTag,
  getStoreWiseDeliveryFeeTags,
  getOnboardingImages,
  getOnBoardingAutoScrollDuration,
  getOnBoardingFleekAutoScroll,
  getBackgroundOnboardingImage,
  getShowFleekSplashScreen,
  getShowFleekOnboardingScreens,
  getShowRevampedNavigation,
  getNavigationConfigs,
  getShowInterstitial,
  getInterstitialMilestonePeriod,
  getSearchBarAnimationCounter,
  getShowCoachMark,
  getChildStores,
  getRemovalLoginPasswordJson,
  getEnableRestorePhoneNumber,
  getAccountCheckApiErrorMsgForSignup,
  getAffiseAfSecure,
  getAjiogramDesktopRedirectBanner,
  getEnableLinkCohort,
  getFleekMiniPLPProductCount,
  getFleekFeedPaginationSize,
  getPdpUiConfiguration,
  getFleekVideoControlConfig,
  getLandingPageFloatingWidget,
  getOnboarding3PVendorsText,
  getEnableReturnProcessingFee,
  getReturnProcessingFeeCartPageText,
  getBlockedForCancellationFlagDesktop,
  getBlockedForCancellationFlagMsite,
  getRemoveAjioWalletRefundDesktop,
  getRemoveAjioWalletRefundMsite,
  getUpiCodRefundFlag,
  getUpiCodRefundDesktopFlag,
  getUpiCodRefundConfig,
  getRetireRCSWallet,
  getRetireRCSWalletLedger,
  getRetireRCSWalletText,
  getRemoveAjioWalletRefundTexts,
  getGA4AJIOEnable,
  getPanCardConfig,
  getEnablePreOrderOnbording3pVendors,
  getReturnCodCommunication,
  getEnableChatWithUsODP,
  getEMIEnable,
  getColorSwatchBrickCode,
  getPdpToBagConversionMasterFlag,
  getPdpToBagConversionConfig,
  getLuxeBottomIconStripUrl,
  getGiftWithProductEnable,
  getGiftWithProductConfig,
  getGwpCartFreeBiesText,
  getGwpCartRemoveCartLevel,
  getGwpCartRemoveProductLevel,
  getGwpConfig,
  getEnableGWPMasterFlag,
  getSocialLogin,
  getPriorityDeliveryPostOrderFlag,
  getPriorityDeliveryPreOrderFlag,
  getPriorityDeliveryConfig,
  getEnableEnchancedOosCart,
  getBarcodingPolyBagConfig,
  getRatingsColorConfig,
  getReturnProcessingFeePostOrderFlag,
  getReturnProcessingFeePostOrderConfig,
  getEnableComingSoonProducts,
  getEnableCODCheck,
  getShowSelectedFiltersTab,
  getDefaultPDPSkuSizeSelectionFlag,
  getEnableAjioMemberShipProgram,
  getSubscriptionBannerConfig,
  getAjioMemberShipConfig,
  getAjioMemberShipConfigMemoized,
  getAppsFlyerConfig,
  getEnableCrossSell,
  getNewShowClosedBoxToOpenBox,
  getAccountDeletionFlag,
  getAccountDeletionFeatureConfig,
  getPDPVideosFeatureConfig,
  getPDPVideosFeatureEnable,
  getEnableOspreySearch,
  getBetterPdpConfigFlag,
  getSectionOneTabularViewFlag,
  getSectionTwoTabularViewFlag,
  getSectionThreeTabularViewFlag,
  getMaxKeyValuePairFlag,
  getMaxLineForValueFlag,
  getBetterPdpTitleFlag,
  getPromoteExchangeWebConfig,
  getPromoteExchangeMsiteConfig,
  getPriorityDeliveryPhase2Flag,
  getPriorityDeliverySLPTagsConfig,
  getAjiogramInterventionsConfig,
  getPaymentModesOnOrderPlacementFlag,
  getRefundCreditModesOnReturnFlag,
  getRefundCreditModesOnCancellationFlag,
  getAppRefundRevampFlowFlag,
  getDownloadBannerText,
  getEnableAjiorgramLandingPage,
  getNewReferralUI,
  getBannerCmsJioAdsConfig,
  getAjioCashMopList,
  getRushDeliveryFlag,
  getRushAvailabilityCookieExpireTimeInHours,
  getGoogleMapsKey,
  getRushCoachMarkConfig,
  getNoDeliveryIconStatuses,
  getReturnExchangeEligibilityNudgeEnable,
  getNewImageCollectionEnable,
  getReturnRequestCalloutEnable,
  getReturnRequestCalloutMessage,
  getReturnCancellationEnable,
  getReturnCancelPopupTitle,
  getReturnCancelPopupDesc,
  getReturnCancelPopupTimelineMsg,
  getReturnCancelPopupSuccessTitle,
  getUserRelevancy,
  getBankDiscountEnabled,
  getBankDiscountCancelDesc,
  getBankDiscountReturnDesc,
  getAjioSuperCashEnable,
  getAjioSuperCashConfig,
  getAjioSuperCashBottomStripConfig,
  getAjioSuperCashCurrencyConfig,
  getAjioSuperCashHomeEnable,
  getAjioSuperCashPDPEnable,
  getAjioSuperCashEnableForCart,
  getAjioSuperCashConfigForCart,
  getAjioSuperCashConfigForPayment,
  getAjioSuperCashODPEnable,
  getAjioSuperCashODPConfig,
  getAjioSuperCashTYPEnable,
  getStoreCredit,
  getAjioSuperCashWalletEnable,
  getAjioSuperCashPdpEnable,
  getAjioSuperCashExpiryTemplate,
  getAjioCashCurrencyName,
  getAjioSuperCashBottomStripConfigPLP,
  getAjioSuperCashBottomStripConfigPDP,
  getAjioSuperCashPlpEnable,
  getEnableVertexSearch,
  getSegmentTrackingEnabledFlag,
  getAssetConfig,
  getPlpOnLandingConfig,
  getPlpOnLandingEnabled,
  getFilterTabsOnLandingEnabled,
  getPlpOnLandingTitleEnabled,
  getWidgetCoverageSearchFlag,
  getWidgetCoveragePDPFlag,
  getPlaAdsOnPDPFlag,
  getPlaAdsFlags,
  getPlaAdsClientID,
  getWidgetCoverageThankYouFlag,
  getPLPProductLoadMargin,
  getCopilotEnabledMsiteFlag,
  getCopilotEnabledDesktopFlag,
  getcopilotWhitelistedPagesMsite,
  getcopilotWhitelistedPagesDesktop,
  getcopilotWhitelistedStoresDesktop,
  getcopilotWhitelistedStoresMsite,
  getDisableDragFlag,
  getHideIngressDesktop,
  getHideIngressMsite,
  getUrgencyDriverV2PLPEnable,
  getUrgencyDriverV2Config,
  getUrgencyDriverV2PDPEnable,
  getUrgencyDriverV2CartEnable,
  getSizeRecommendationConfig,
  getGSTFlag,
  getGSTCommonConfig,
  getGSTCartConfig,
  getGSTODPConfig,
  getGstEnhancementConfig,
  getGstEnhancementFlag,
  getOrderConfirmGstEnhancementTextConfig,
  getShippingGstEnhancementTextConfig,
  bank_discount_clawback_config,
  getEnableFilterImprovement,
  getFilterImprovementConfig,
  getEmiDiscountEnable,
  getEmiDiscountEnableForPDP,
  getEmiTemplate,
  getEmiThresholdAmount,
  getOfferCardOrder,
  getViewMoreEmiConfig,
  getEmiDiscountSPCConfig,
  getRatingParityMsiteFlag,
  getOosSimilarProductFlag,
  getEnableFilterImprovementPhase2,
  getIsLimitedScrollLandingPageEnabled,
  getLimitedScrollLandingPageConfig,
  getEmiDiscountEnableForSPC,
  getAffiliateFeatureConfig,
  getAffiliateAttributionWindow,
  getJioAdsLandingBannersFlag,
  getEnableAdsOnSlp,
  getEnableAdsOnPlp,
  getBannerOsmosAdsConfig,
  getCrossSellV2CartEnable,
  getCrossSellV2CartPageConfig,
  getCompanionMsiteConfig,
  getTrendBasedDiscoveryConfig,
  getTrendBasedDiscoveryFlag,
  getVertexEnabledAjiogram,
  getVertexEnabledLuxe,
  getCrossSellV2Enabled,
  getVisualFiltersConfig,
  getRedemptionOptionsOnCartFlag,
  getCustomerCelebrationConfig,
  getOLPFilterImprovementConfig,
  getVertexEnabledForSis,
  getCancelRefundImprovementConfig,
  getIsCancelRefundImprovementEnabled,
  getEnableBestCouponOnCart,
  getEnableNonReturnableProductRevamp,
  getEnableNonReturnableProductRevampConfig,
  getjioVaultConfig,
  getKaptureChatConfig,
  getOrderDetailsJcpMigrationConfig,
  getCreatorProgramConfig,
  getCountDisplayConfig,
  getSearchMessagingConfig
};
